import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { safeImageUrl } from "../utils/escapeHtml";
import {
  fetchReservations, createReservation, cancelReservation, noShowReservation, reinstateReservation, moveRoom, changeStay, updateReservation,
  setReservationDiscount, checkInReservation, checkOutReservation,
  addReservationCharge, deleteReservationCharge, transferReservationCharge, markChargeSigned, requestChargeApproval, fetchReservationReceipt, fetchReviewLink,
  fetchGuestServices, addGuestService, updateGuestService, deleteGuestService,
  fetchRoomTypes, fetchRooms, fetchRoomAvailability, resolveRatePlan, fetchCompanies, getRole, getCurrency,
  fetchGuestHistory, searchGuests, hasFeature, fetchUpsellOptions, addUpsell, addStayFeedback, getPrecheckinLink, getReservationIdImage,
  getMessagingStatus, setupMessaging, getOptInLink, sendPrecheckinMessage, sendGuestMessage, fetchGuestMessages,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";
import RoomPicker from "../components/RoomPicker";

// Front Desk — Slice 2a: reservations (bookings & stays).
const S = {
  en: {
    title: "Reservations", subtitle: "Bookings, arrivals and departures",
    v_arrivals: "Arrivals", v_departures: "Departures", v_inhouse: "In-house", v_upcoming: "Upcoming", v_all: "All",
    new_booking: "New booking", none: "Nothing here.",
    guest: "Guest name", phone: "Phone", email: "Email", origin: "From (city / country)", nationality: "Nationality",
    fname: "First name", mname: "Middle name", lname: "Last name", gender: "Gender", male: "Male", female: "Female", g_other: "Other", language: "Language", complimentary: "Complimentary (no room charge)",
    returning: "Returning guest", stays: "stays", prefers: "prefers", owes: "owes", use_details: "Use details",
    upsell: "Upsell", extra_l: "Extra", commission: "Commission %", addU: "Add",
    feedback: "Guest feedback", overall: "Overall", staff_r: "Staff", fb_comment: "Comment", save_fb: "Save",
    precheckin_link: "Check-in link", precheckin_badge: "Pre-checked-in", arrival_time_l: "Arrival", special_req_l: "Requests",
    link_hint: "Send this link to the guest", copy: "Copy", copied: "Copied", close_l: "Close", view_id: "View ID photo",
    message_l: "Message", tg_title: "Message guest (Telegram)", tg_notlinked: "Guest hasn't connected yet. Share this link so they tap Start in Telegram:",
    connect_l: "Connect", connect_title: "Connect guest on Telegram", connect_hint: "Ask the guest to scan this with their phone camera, open the bot and press Start.", connected_l: "Connected",
    tg_setup_first: "Set up Telegram messaging first (top of page).", tg_send_checkin: "Send check-in link",
    tg_tpl1: "Checkout reminder", tg_tpl2: "Breakfast hours", tg_tpl3: "Welcome note", tg_placeholder: "Type a message to the guest…", tg_send: "Send message",
    tg_setup_title: "Set up guest messaging (Telegram)", tg_setup_help: "Create a bot in Telegram with @BotFather, then paste its token here. Free, one-time.",
    tg_token_ph: "Bot token from @BotFather", tg_connect: "Connect", tg_connected: "Telegram connected:",
    id_type: "ID type", id_number: "ID number", id_kebele: "Kebele ID", id_fayda: "Fayda", id_passport: "Passport", id_other: "Other", id_none: "—",
    source: "Booking source", src_walk_in: "Walk-in", src_phone: "Phone", src_corporate: "Corporate",
    company: "Company name", company_tin: "Company TIN", room_type: "Room type", pick_type: "Select room type",
    room: "Room", pick_room: "Assign a room (optional)",
    addr_title: "Address", country: "Country", c_eth: "Ethiopia", c_other: "Other country",
    region: "Region", city: "City / Sub-city", woreda: "Woreda", kebele: "Kebele",
    street: "Street / House no.", post_code: "Post code",
    check_in: "Check-in", check_out: "Check-out", adults: "Adults", children: "Children",
    breakfast: "Breakfast included", cancel_policy: "Cancellation policy (optional)",
    save: "Save booking", cancel: "Cancel", nights: "nights", night: "night",
    total: "Total", balance: "Balance", per_night: "/night", bill_company: "Bill to company",
    st_booked: "Booked", st_checked_in: "In-house", st_checked_out: "Checked out", st_cancelled: "Cancelled", st_no_show: "No-show", st_waitlist: "Waiting list",
    act_cancel: "Cancel", act_no_show: "No-show", act_reinstate: "Reinstate", hold6: "6PM hold", needs_room: "No room", cancel_q: "Cancel this booking? Reason (optional):", noshow_q: "Mark as no-show?",
    name_req: "Guest name is required.", date_req: "Check-out must be after check-in.", could_not: "Could not save. Check the dates and try again.",
    check_in: "Check in", check_out: "Check out", discount: "Discount", confirm: "Confirm",
    reference: "Reference / txn no.", amount: "Amount", ci_title: "Check-in", co_title: "Check-out",
    pm_cash: "Cash", pm_telebirr: "Telebirr", pm_cbe_birr: "CBE Birr", pm_pos: "Card / POS", pm_other: "Other",
    assign_room_req: "Please choose a room.", room_busy: "That room is occupied — pick another.", assign_room: "Assign room",
    move_room: "Move room", extend_stay: "Extend / shorten", move_title: "Move to another room", extend_title: "Change stay dates", new_checkout: "New check-out", room_ooo_m: "That room is out of order.", bad_dates_m: "Check-out must be after check-in.",
    av_available: "Available", av_booked: "Booked", av_occupied: "Occupied", av_ooo: "Out of order",
    pin_q: "Manager PIN to approve this discount:", disc_amount_q: "Discount amount (birr):", disc_reason_q: "Reason for the discount:",
    folio: "Folio", receipt: "Receipt", room_line: "Room", add_charge: "Add charge", charge_desc: "Description", charge_amt: "Amount",
    k_fb: "Restaurant/Bar", k_laundry: "Laundry", k_minibar: "Minibar", k_transport: "Transport", k_other: "Other", kind: "Type",
    k_paid_out: "Paid-out", k_rebate: "Rebate (credit)", k_refund: "Refund (credit)",
    service_charge_l: "Service charge", tax_l: "Tax",
    transfer: "Transfer", transfer_to: "Move this charge to:", transfer_none: "No other in-house guest to transfer to.", transfer_pick: "Select a guest…",
    services: "Services", no_services: "No services yet.", add_service: "Add", sv_detail: "Detail",
    sv_wakeup: "Wakeup", sv_message: "Message", sv_request: "Request", done_l: "Done", reopen: "Reopen",
    charges_total: "Extras", subtotal: "Subtotal", discount_l: "Discount", grand_total: "Total",
    cat_accommodation: "Accommodation", cat_fnb: "Food & Beverage", cat_wellness: "Wellness", cat_roomservice: "Room service", cat_other: "Other", group_subtotal: "Subtotal",
    v_title: "Charge to room", v_not_receipt: "Not a payment receipt", v_room: "Room", v_guest: "Guest",
    v_total: "Total charged to room", v_sign: "Guest signature", v_settle: "Charged to the room account — settle at checkout.",
    signed: "Signed", approved_app: "Approved (app)", mark_signed: "Mark signed", request_approval: "Request approval",
    not_connected_hint: "Guest isn't connected on Telegram — share the opt-in link first.", approval_sent: "Approval request sent to the guest.", approval_failed: "Could not send the approval request.",
    edit: "Edit", edit_title: "Edit guest details", save_edit: "Save changes",
    customer_tin: "Customer TIN", bill_to_name: "Bill-to name / company", print_bill: "Print bill", tin_prompt: "Customer TIN (for the invoice)",
    review_scan: "Scan to review your stay on Google",
  },
  am: {
    title: "ማስያዣዎች", subtitle: "ቦታ ማስያዣ፣ መግቢያና መውጫ",
    v_arrivals: "መግቢያዎች", v_departures: "መውጫዎች", v_inhouse: "በውስጥ ያሉ", v_upcoming: "የሚመጡ", v_all: "ሁሉም",
    new_booking: "አዲስ ማስያዣ", none: "እዚህ ምንም የለም።",
    guest: "የእንግዳ ስም", phone: "ስልክ", email: "ኢሜይል", origin: "ከየት (ከተማ / ሀገር)", nationality: "ዜግነት",
    fname: "የመጀመሪያ ስም", mname: "የአባት ስም", lname: "የአያት ስም", gender: "ጾታ", male: "ወንድ", female: "ሴት", g_other: "ሌላ", language: "ቋንቋ", complimentary: "የክብር እንግዳ (ክፍያ የለም)",
    returning: "ተመላሽ እንግዳ", stays: "ቆይታዎች", prefers: "ይመርጣል", owes: "ዕዳ", use_details: "መረጃዎችን ተጠቀም",
    upsell: "ተጨማሪ ሽያጭ", extra_l: "ተጨማሪ", commission: "ኮሚሽን %", addU: "ጨምር",
    feedback: "የእንግዳ አስተያየት", overall: "አጠቃላይ", staff_r: "ሰራተኛ", fb_comment: "አስተያየት", save_fb: "አስቀምጥ",
    precheckin_link: "የመግቢያ ሊንክ", precheckin_badge: "አስቀድሞ ተመዝግቧል", arrival_time_l: "መድረሻ ሰዓት", special_req_l: "ጥያቄዎች",
    link_hint: "ይህን ሊንክ ለእንግዳው ይላኩ", copy: "ቅዳ", copied: "ተቀድቷል", close_l: "ዝጋ", view_id: "የመታወቂያ ፎቶ ይመልከቱ",
    message_l: "መልእክት", tg_title: "ለእንግዳ መልእክት (ቴሌግራም)", tg_notlinked: "እንግዳው ገና አልተገናኘም። ይህን ሊንክ ልከው በቴሌግራም Start እንዲነኩ ያድርጉ፦",
    connect_l: "አገናኝ", connect_title: "እንግዳን በቴሌግራም አገናኝ", connect_hint: "እንግዳው በስልኩ ካሜራ ይህን እንዲቃኝ፣ ቦቱን ከፍቶ Start እንዲነካ ይጠይቁ።", connected_l: "ተገናኝቷል",
    tg_setup_first: "መጀመሪያ የቴሌግራም መልእክት ያዋቅሩ (ከላይ)።", tg_send_checkin: "የመግቢያ ሊንክ ላክ",
    tg_tpl1: "የመውጫ ማስታወሻ", tg_tpl2: "የቁርስ ሰዓት", tg_tpl3: "እንኳን ደህና መጡ", tg_placeholder: "ለእንግዳው መልእክት ይጻፉ…", tg_send: "መልእክት ላክ",
    tg_setup_title: "የእንግዳ መልእክት ያዋቅሩ (ቴሌግራም)", tg_setup_help: "በቴሌግራም @BotFather ቦት ይፍጠሩ፣ ከዚያ ቶከኑን እዚህ ይለጥፉ። ነጻ፣ አንድ ጊዜ።",
    tg_token_ph: "ከ@BotFather የተገኘ ቶከን", tg_connect: "አገናኝ", tg_connected: "ቴሌግራም ተገናኝቷል፦",
    id_type: "የመታወቂያ ዓይነት", id_number: "የመታወቂያ ቁጥር", id_kebele: "የቀበሌ መታወቂያ", id_fayda: "ፋይዳ", id_passport: "ፓስፖርት", id_other: "ሌላ", id_none: "—",
    source: "የማስያዣ ምንጭ", src_walk_in: "በአካል የመጣ", src_phone: "ስልክ", src_corporate: "ድርጅት",
    company: "የድርጅት ስም", company_tin: "የድርጅት ቲን", room_type: "የክፍል ዓይነት", pick_type: "የክፍል ዓይነት ይምረጡ",
    room: "ክፍል", pick_room: "ክፍል መድብ (አማራጭ)",
    addr_title: "አድራሻ", country: "ሀገር", c_eth: "ኢትዮጵያ", c_other: "ሌላ ሀገር",
    region: "ክልል", city: "ከተማ / ክፍለ ከተማ", woreda: "ወረዳ", kebele: "ቀበሌ",
    street: "መንገድ / የቤት ቁ.", post_code: "ፖስታ ኮድ",
    check_in: "መግቢያ", check_out: "መውጫ", adults: "አዋቂዎች", children: "ልጆች",
    breakfast: "ቁርስ ተካትቷል", cancel_policy: "የስረዛ ፖሊሲ (አማራጭ)",
    save: "ማስያዣ አስቀምጥ", cancel: "ሰርዝ", nights: "ሌሊቶች", night: "ሌሊት",
    total: "ጠቅላላ", balance: "ቀሪ", per_night: "/ሌሊት", bill_company: "ለድርጅት ይከፈል",
    st_booked: "ተይዟል", st_checked_in: "በውስጥ", st_checked_out: "ወጥቷል", st_cancelled: "ተሰርዟል", st_no_show: "አልመጣም", st_waitlist: "መጠባበቂያ",
    act_cancel: "ሰርዝ", act_no_show: "አልመጣም", act_reinstate: "መልስ", hold6: "6PM ማቆያ", needs_room: "ክፍል የለም", cancel_q: "ይህን ማስያዣ መሰረዝ? ምክንያት (አማራጭ):", noshow_q: "እንደ አልመጣም ምልክት ማድረግ?",
    name_req: "የእንግዳ ስም ያስፈልጋል።", date_req: "መውጫ ከመግቢያ በኋላ መሆን አለበት።", could_not: "ማስቀመጥ አልተቻለም። ቀኖቹን ፈትሸው እንደገና ይሞክሩ።",
    check_in: "አስገባ", check_out: "አስወጣ", discount: "ቅናሽ", confirm: "አረጋግጥ",
    reference: "ማጣቀሻ / የግብይት ቁ.", amount: "መጠን", ci_title: "መግቢያ", co_title: "መውጫ",
    pm_cash: "ጥሬ ገንዘብ", pm_telebirr: "ቴሌብር", pm_cbe_birr: "ሲቢኢ ብር", pm_pos: "ካርድ / POS", pm_other: "ሌላ",
    assign_room_req: "እባክዎ ክፍል ይምረጡ።", room_busy: "ያ ክፍል ተይዟል — ሌላ ይምረጡ።", assign_room: "ክፍል መድብ",
    move_room: "ክፍል ቀይር", extend_stay: "አራዝም / አሳጥር", move_title: "ወደ ሌላ ክፍል አዛውር", extend_title: "የቆይታ ቀኖች ቀይር", new_checkout: "አዲስ መውጫ", room_ooo_m: "ያ ክፍል ከአገልግሎት ውጪ ነው።", bad_dates_m: "መውጫ ከመግቢያ በኋላ መሆን አለበት።",
    av_available: "ክፍት", av_booked: "ተይዟል", av_occupied: "በውስጥ", av_ooo: "ከአገልግሎት ውጪ",
    pin_q: "ቅናሹን ለማጽደቅ የሥራ አስኪያጅ ፒን:", disc_amount_q: "የቅናሽ መጠን (ብር):", disc_reason_q: "የቅናሽ ምክንያት:",
    folio: "ሂሳብ", receipt: "ደረሰኝ", room_line: "ክፍል", add_charge: "ክፍያ ጨምር", charge_desc: "መግለጫ", charge_amt: "መጠን",
    k_fb: "ምግብ ቤት/ባር", k_laundry: "ልብስ ማጠቢያ", k_minibar: "ሚኒባር", k_transport: "ትራንስፖርት", k_other: "ሌላ", kind: "ዓይነት",
    k_paid_out: "የተከፈለ (ለእንግዳ)", k_rebate: "ቅናሽ (ክሬዲት)", k_refund: "ተመላሽ (ክሬዲት)",
    service_charge_l: "የአገልግሎት ክፍያ", tax_l: "ታክስ",
    transfer: "አዛውር", transfer_to: "ይህን ክፍያ ወደዚህ አዛውር:", transfer_none: "የሚዛወርለት ሌላ የውስጥ እንግዳ የለም።", transfer_pick: "እንግዳ ይምረጡ…",
    services: "አገልግሎቶች", no_services: "እስካሁን አገልግሎት የለም።", add_service: "ጨምር", sv_detail: "ዝርዝር",
    sv_wakeup: "ማንቂያ", sv_message: "መልእክት", sv_request: "ጥያቄ", done_l: "ተከናውኗል", reopen: "መልስ ክፈት",
    charges_total: "ተጨማሪዎች", subtotal: "ንዑስ ድምር", discount_l: "ቅናሽ", grand_total: "ጠቅላላ",
    cat_accommodation: "ማረፊያ", cat_fnb: "ምግብና መጠጥ", cat_wellness: "ጤናና እንክብካቤ", cat_roomservice: "የክፍል አገልግሎት", cat_other: "ሌላ", group_subtotal: "ንዑስ ድምር",
    v_title: "ወደ ክፍል አስከፍል", v_not_receipt: "የክፍያ ደረሰኝ አይደለም", v_room: "ክፍል", v_guest: "እንግዳ",
    v_total: "ወደ ክፍል የተከፈለ ጠቅላላ", v_sign: "የእንግዳ ፊርማ", v_settle: "ወደ ክፍል ሂሳብ ተከፍሏል — ሲወጡ ያወራርዱ።",
    signed: "ተፈርሟል", approved_app: "ጸድቋል (መተግበሪያ)", mark_signed: "እንደተፈረመ ምልክት አድርግ", request_approval: "ማጽደቅ ጠይቅ",
    not_connected_hint: "እንግዳው በቴሌግራም አልተገናኘም — መጀመሪያ የመቀላቀያ ማገናኛውን ያጋሩ።", approval_sent: "የማጽደቅ ጥያቄ ለእንግዳው ተልኳል።", approval_failed: "የማጽደቅ ጥያቄ መላክ አልተቻለም።",
    edit: "አስተካክል", edit_title: "የእንግዳ ዝርዝር አስተካክል", save_edit: "ለውጦችን አስቀምጥ",
    customer_tin: "የደንበኛ ቲን", bill_to_name: "የከፋይ ስም / ድርጅት", print_bill: "ሂሳብ አትም", tin_prompt: "የደንበኛ ቲን (ለደረሰኝ)",
    review_scan: "ቆይታዎን በ Google ለመገምገም ይቃኙ",
  },
  om: {
    title: "Qabannoowwan", subtitle: "Qabannoo, seensaa fi ba'uu",
    v_arrivals: "Seensota", v_departures: "Ba'uuwwan", v_inhouse: "Keessa jiran", v_upcoming: "Kan dhufan", v_all: "Hunda",
    new_booking: "Qabannoo haaraa", none: "Asitti homaa hin jiru.",
    guest: "Maqaa keessummaa", phone: "Bilbila", email: "Imeelii", origin: "Eessaa (magaalaa / biyya)", nationality: "Lammummaa",
    fname: "Maqaa jalqabaa", mname: "Maqaa abbaa", lname: "Maqaa akaakayyuu", gender: "Saala", male: "Dhiira", female: "Dubartii", g_other: "Kan biraa", language: "Afaan", complimentary: "Keessummaa kabajaa (kaffaltii hin qabu)",
    returning: "Keessummaa deebi'e", stays: "turtii", prefers: "filata", owes: "qaba", use_details: "Odeeffannoo fayyadami",
    upsell: "Gurgurtaa dabalataa", extra_l: "Dabalata", commission: "Komiishinii %", addU: "Dabali",
    feedback: "Yaada keessummaa", overall: "Waliigala", staff_r: "Hojjetaa", fb_comment: "Yaada", save_fb: "Olkaa'i",
    precheckin_link: "Liinkii galmee", precheckin_badge: "Duraan galmaa'e", arrival_time_l: "Yeroo ga'umsaa", special_req_l: "Gaaffiiwwan",
    link_hint: "Liinkii kana keessummaadhaaf ergi", copy: "Garagalchi", copied: "Garagalfame", close_l: "Cufi", view_id: "Suuraa eenyummaa ilaali",
    message_l: "Ergaa", tg_title: "Keessummaadhaaf ergaa (Telegram)", tg_notlinked: "Keessummaan hin walqunnamne. Liinkii kana ergiitii Telegram irratti Start akka tuqan godhi:",
    connect_l: "Walqunnamsiisi", connect_title: "Keessummaa Telegramiin walqunnamsiisi", connect_hint: "Keessummaan kaameeraa bilbila isaatiin kana akka scan godhu, boot banee Start akka tuqu gaafadhu.", connected_l: "Walqunnameera",
    tg_setup_first: "Duraan Telegram messaging qopheessi (gubbaa).", tg_send_checkin: "Liinkii galmee ergi",
    tg_tpl1: "Yaadachiisa ba'umsaa", tg_tpl2: "Sa'aatii ciree", tg_tpl3: "Baga nagaan dhufte", tg_placeholder: "Keessummaadhaaf ergaa barreessi…", tg_send: "Ergaa ergi",
    tg_setup_title: "Keessummaa messaging qopheessi (Telegram)", tg_setup_help: "Telegram keessatti @BotFather waliin bot uumi, ergasii tokenii isaa asitti maxxansi. Bilisa, al tokko.",
    tg_token_ph: "Token @BotFather irraa", tg_connect: "Walqunnamsiisi", tg_connected: "Telegram walqunnameera:",
    id_type: "Gosa waraqaa", id_number: "Lakk. waraqaa", id_kebele: "Waraqaa Kebele", id_fayda: "Fayda", id_passport: "Paaspoortii", id_other: "Kan biraa", id_none: "—",
    source: "Madda qabannoo", src_walk_in: "Kallattiin dhufe", src_phone: "Bilbila", src_corporate: "Dhaabbata",
    company: "Maqaa dhaabbata", company_tin: "TIN dhaabbata", room_type: "Gosa kutaa", pick_type: "Gosa kutaa fili",
    room: "Kutaa", pick_room: "Kutaa ramadi (filannoo)",
    addr_title: "Teessoo", country: "Biyya", c_eth: "Itoophiyaa", c_other: "Biyya biraa",
    region: "Naannoo", city: "Magaalaa / Kutaa magaalaa", woreda: "Aanaa", kebele: "Ganda",
    street: "Daandii / Lakk. manaa", post_code: "Koodii poostaa",
    check_in: "Seensa", check_out: "Ba'uu", adults: "Ga'eeyyii", children: "Ijoollee",
    breakfast: "Ciree dabalatee", cancel_policy: "Imaammata haquu (filannoo)",
    save: "Qabannoo kaa'i", cancel: "Haqi", nights: "halkanoota", night: "halkan",
    total: "Ida'ama", balance: "Hafe", per_night: "/halkan", bill_company: "Dhaabbataaf",
    st_booked: "Qabame", st_checked_in: "Keessa", st_checked_out: "Ba'e", st_cancelled: "Haqame", st_no_show: "Hin dhufne", st_waitlist: "Eeggannoo",
    act_cancel: "Haqi", act_no_show: "Hin dhufne", act_reinstate: "Deebisi", hold6: "Eeggannoo 6PM", needs_room: "Kutaa hin jiru", cancel_q: "Qabannoo kana haquu? Sababa (filannoo):", noshow_q: "Akka hin dhufneetti mallatteessuu?",
    name_req: "Maqaan keessummaa barbaachisaa dha.", date_req: "Ba'uun seensa booda ta'uu qaba.", could_not: "Kaa'uu hin dandeenye. Guyyaa ilaalii irra deebi'i.",
    check_in: "Galchi", check_out: "Baasi", discount: "Hir'isa", confirm: "Mirkaneessi",
    reference: "Wabii / lakk. daldalaa", amount: "Hanga", ci_title: "Seensa", co_title: "Ba'uu",
    pm_cash: "Maallaqa", pm_telebirr: "Telebirr", pm_cbe_birr: "CBE Birr", pm_pos: "Kaardii / POS", pm_other: "Kan biraa",
    assign_room_req: "Maaloo kutaa fili.", room_busy: "Kutaan sun qabameera — kan biraa fili.", assign_room: "Kutaa ramadi",
    move_room: "Kutaa jijjiiri", extend_stay: "Dheeressi / gabaabsi", move_title: "Gara kutaa biraa dabarsi", extend_title: "Guyyaa turtii jijjiiri", new_checkout: "Ba'uu haaraa", room_ooo_m: "Kutaan sun tajaajila ala jira.", bad_dates_m: "Ba'uun seensa booda ta'uu qaba.",
    av_available: "Banaa", av_booked: "Qabame", av_occupied: "Keessa", av_ooo: "Tajaajila ala",
    pin_q: "Hir'isa kana mirkaneessuuf PIN hooggantuu:", disc_amount_q: "Hanga hir'isaa (birrii):", disc_reason_q: "Sababa hir'isaa:",
    folio: "Herrega", receipt: "Nagahee", room_line: "Kutaa", add_charge: "Kaffaltii dabali", charge_desc: "Ibsa", charge_amt: "Hanga",
    k_fb: "Restoora/Baar", k_laundry: "Miccaa", k_minibar: "Minibar", k_transport: "Geejjiba", k_other: "Kan biraa", kind: "Gosa",
    k_paid_out: "Kaffaltii ba'e", k_rebate: "Hir'isa (kireeditii)", k_refund: "Deebii (kireeditii)",
    service_charge_l: "Kaffaltii tajaajilaa", tax_l: "Gibira",
    transfer: "Dabarsi", transfer_to: "Kaffaltii kana gara:", transfer_none: "Keessummaan biraa mana keessa jiru hin jiru.", transfer_pick: "Keessummaa filadhu…",
    services: "Tajaajiloota", no_services: "Ammatti tajaajilli hin jiru.", add_service: "Dabali", sv_detail: "Ibsa",
    sv_wakeup: "Dammaqsuu", sv_message: "Ergaa", sv_request: "Gaaffii", done_l: "Xumurame", reopen: "Deebisi bani",
    charges_total: "Dabalataa", subtotal: "Ida'ama xiqqaa", discount_l: "Hir'isa", grand_total: "Ida'ama",
    cat_accommodation: "Bakka bultii", cat_fnb: "Nyaata & Dhugaatii", cat_wellness: "Fayyaa & Boqonnaa", cat_roomservice: "Tajaajila kutaa", cat_other: "Kan biraa", group_subtotal: "Ida'ama xiqqaa",
    v_title: "Kutaatti kaffalchiisi", v_not_receipt: "Nagahee kaffaltii miti", v_room: "Kutaa", v_guest: "Keessummaa",
    v_total: "Ida'ama kutaatti kaffalame", v_sign: "Mallattoo keessummaa", v_settle: "Herrega kutaatti kaffalame — yeroo ba'an kaffali.",
    signed: "Mallatteeffame", approved_app: "Mirkanaa'e (app)", mark_signed: "Akka mallatteeffametti mallatteessi", request_approval: "Mirkaneessa gaafadhu",
    not_connected_hint: "Keessummaan Telegramiin walqunnamee hin jiru — dursa liinkii walqunnamtii qooddadhu.", approval_sent: "Gaaffiin mirkaneessaa keessummaadhaaf ergameera.", approval_failed: "Gaaffii mirkaneessaa erguun hin danda'amne.",
    edit: "Sirreessi", edit_title: "Odeeffannoo keessummaa sirreessi", save_edit: "Jijjiirama olkaa'i",
    customer_tin: "TIN maamilaa", bill_to_name: "Maqaa kaffalaa / dhaabbata", print_bill: "Herrega maxxansi", tin_prompt: "TIN maamilaa (nagahee)",
    review_scan: "Turtii kee Google irratti gamaaggamuuf skaanii godhi",
  },
};

function escapeHtml(s) {
  return String(s == null ? "" : s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}

const CARD = { background: "#fff", border: "1px solid #eef2f2", borderRadius: 14, boxShadow: "0 1px 3px rgba(6,59,72,0.06)" };
const STMETA = {
  waitlist: { bg: "#F3EFE0", fg: "#8A6D3B" },
  booked: { bg: "#E6F1FB", fg: "#185FA5" },
  checked_in: { bg: "#E7F6EC", fg: "#1D7A44" },
  checked_out: { bg: "#EFEFF2", fg: "#5b6472" },
  cancelled: { bg: "#FCEBEB", fg: "#A32D2D" },
  no_show: { bg: "#FAEEDA", fg: "#8A5A0B" },
};
const VIEWS = ["arrivals", "departures", "inhouse", "upcoming", "all"];

// Currency list for foreign-currency bookings. Shown in a searchable datalist
// so the desk can type "eur" / "euro" / "dollar" and pick — the stored value is
// always the ISO code (keeps the Forex report grouped cleanly). Common ones for
// an Ethiopian hotel first, then the rest of the widely-held currencies.
const FX_CURRENCIES = [
  ["ETB", "Ethiopian Birr"], ["USD", "US Dollar"], ["EUR", "Euro"], ["GBP", "British Pound"],
  ["AED", "UAE Dirham"], ["SAR", "Saudi Riyal"], ["QAR", "Qatari Riyal"], ["KWD", "Kuwaiti Dinar"],
  ["CNY", "Chinese Yuan"], ["JPY", "Japanese Yen"], ["CHF", "Swiss Franc"], ["CAD", "Canadian Dollar"],
  ["AUD", "Australian Dollar"], ["INR", "Indian Rupee"], ["KES", "Kenyan Shilling"], ["DJF", "Djibouti Franc"],
  ["TRY", "Turkish Lira"], ["ZAR", "South African Rand"], ["SEK", "Swedish Krona"], ["NOK", "Norwegian Krone"],
  ["DKK", "Danish Krone"], ["RUB", "Russian Ruble"], ["EGP", "Egyptian Pound"], ["NGN", "Nigerian Naira"],
  ["BHD", "Bahraini Dinar"], ["OMR", "Omani Rial"], ["ILS", "Israeli Shekel"], ["SGD", "Singapore Dollar"],
  ["HKD", "Hong Kong Dollar"], ["KRW", "South Korean Won"], ["BRL", "Brazilian Real"], ["MXN", "Mexican Peso"],
  ["NZD", "New Zealand Dollar"], ["PLN", "Polish Zloty"], ["CZK", "Czech Koruna"], ["THB", "Thai Baht"],
  ["MAD", "Moroccan Dirham"], ["TZS", "Tanzanian Shilling"], ["UGX", "Ugandan Shilling"], ["SDG", "Sudanese Pound"],
];

function todayISO(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return d.toISOString().slice(0, 10);
}
const emptyForm = () => ({
  guest_name: "", title: "", first_name: "", middle_name: "", last_name: "", gender: "", date_of_birth: "", language: "", complimentary: false,
  guest_phone: "", guest_phone2: "", guest_email: "", origin: "", nationality: "",
  id_type: "", id_number: "", source: "walk_in", market: "", agent: "", company_name: "", bill_to_tin: "", company_account_id: "", perdiem_company_account_id: "", company_hold: false, consent_given: false,
  room_type_id: "", room_id: "", check_in_date: todayISO(0), check_out_date: todayISO(1),
  adults: 1, children: 0, occupancy: "single", meal_plan: "ro",
  hold_type: "guaranteed", waitlist: false, residency: "local", fx_currency: "ETB", fx_rate: "",
  rate_per_night: "", rack_rate: "", breakfast_included: false, cancellation_policy: "",
  address_country: "Ethiopia", region: "", city: "", sub_city: "", woreda: "", kebele: "", street: "", post_code: "",
});

export default function ReservationsPage() {
  const { lang } = useT();
  const t = (k) => (S[lang] && S[lang][k]) || S.en[k] || k;
  const role = getRole();
  const canEdit = role === "manager" || role === "front_desk";
  const cur = getCurrency() || "ETB";

  const [view, setView] = useState("arrivals");
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");   // search rooms/guests in the current list
  const [types, setTypes] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [err, setErr] = useState("");
  const [panel, setPanel] = useState(null);       // { id, type: "checkin"|"checkout"|"move"|"extend" }
  const [pf, setPf] = useState({ room_id: "", method: "cash", reference: "", amount: "", guest_name: "" });
  const [mf, setMf] = useState({ room_id: "", check_out_date: "" });   // move / extend form
  const [panelErr, setPanelErr] = useState("");
  const [folioId, setFolioId] = useState(null);
  const [cf, setCf] = useState({ description: "", amount: "", kind: "fb", source: "other" });
  const [xfer, setXfer] = useState(null);   // { cid, target } — bill transfer
  const [svcId, setSvcId] = useState(null); // reservation whose services panel is open
  const [svcList, setSvcList] = useState([]);
  const [sf, setSf] = useState({ kind: "request", detail: "", due_at: "" });
  const [availBooking, setAvailBooking] = useState(null);
  const [availCheckin, setAvailCheckin] = useState(null);
  const [editing, setEditing] = useState(null);       // reservation id whose guest-edit panel is open
  const [ef, setEf] = useState({});                    // edit form
  const [editErr, setEditErr] = useState("");
  const [tinFor, setTinFor] = useState(null);          // reservation id whose TIN-before-print prompt is open
  const [tf, setTf] = useState({ bill_to_tin: "", company_name: "" });

  const load = () => { setRows(null); fetchReservations(view).then(setRows).catch(() => setRows([])); };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view]);
  useEffect(() => {
    fetchRoomTypes().then(setTypes).catch(() => setTypes([]));
    fetchRooms().then(setRooms).catch(() => setRooms([]));
    fetchCompanies().then(setCompanies).catch(() => setCompanies([]));
  }, []);

  // Room availability for the booking form's dates (annotates the room picker).
  useEffect(() => {
    if (adding && form.check_in_date && form.check_out_date && form.check_out_date > form.check_in_date) {
      fetchRoomAvailability(form.check_in_date, form.check_out_date, form.room_type_id ? Number(form.room_type_id) : null)
        .then(setAvailBooking).catch(() => setAvailBooking(null));
    }
    // eslint-disable-next-line
  }, [adding, form.check_in_date, form.check_out_date, form.room_type_id]);

  // Auto-resolve the nightly rate from the rate plan for the chosen room type,
  // occupancy and meal plan (falls back to the room type's base rate).
  useEffect(() => {
    if (!adding || !form.room_type_id) return;
    let cancelled = false;
    const _base = () => {
      const tp = types.find((x) => String(x.id) === String(form.room_type_id));
      return tp && tp.base_rate != null ? tp.base_rate : null;
    };
    resolveRatePlan(Number(form.room_type_id), form.occupancy, form.meal_plan)
      .then((r) => {
        if (cancelled) return;
        const base = _base();
        // Use the matching rate plan if there is one, otherwise fall back to
        // the room type's base rate so the price is always pre-filled (editable).
        const amount = (r && r.amount != null) ? r.amount : base;
        const rack = (r && r.rack_rate != null) ? r.rack_rate : amount;
        setForm((f) => ({
          ...f,
          rate_per_night: amount != null ? String(amount) : "",
          rack_rate: rack != null ? String(rack) : "",
        }));
      })
      .catch(() => {
        if (cancelled) return;
        const base = _base();
        if (base != null) setForm((f) => ({ ...f, rate_per_night: String(base), rack_rate: String(base) }));
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [adding, form.room_type_id, form.occupancy, form.meal_plan]);

  const [showMore, setShowMore] = useState(false);
  const OCC_OPTS = [
    { v: "single", label: { en: "Single", am: "ነጠላ", om: "Nama tokko" } },
    { v: "double", label: { en: "Double", am: "ድርብ", om: "Nama lama" } },
  ];
  const MEAL_OPTS = [
    { v: "ro", label: { en: "Room only", am: "ክፍል ብቻ", om: "Kutaa qofa" } },
    { v: "bb", label: { en: "B&B", am: "ከቁርስ ጋር", om: "Ciree waliin" } },
    { v: "hb", label: { en: "Half board", am: "ግማሽ ቦርድ", om: "Boordii walakkaa" } },
    { v: "fb", label: { en: "Full board", am: "ሙሉ ቦርድ", om: "Boordii guutuu" } },
  ];
  const optLabel = (o) => o.label[lang] || o.label.en;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // D1 — returning-guest recognition (add-on: guest_recognition). Look the guest
  // up by phone / email / ID as those fields lose focus and surface a banner.
  const recogOn = hasFeature("guest_recognition");
  const [ghist, setGhist] = useState(null);
  const lookupGuest = () => {
    if (!recogOn) return;
    const params = {};
    if (form.guest_phone) params.phone = form.guest_phone;
    if (form.guest_email) params.email = form.guest_email;
    if (form.id_number) params.id_number = form.id_number;
    if (!params.phone && !params.email && !params.id_number) { setGhist(null); return; }
    fetchGuestHistory(params).then((r) => setGhist(r && r.match ? r : null)).catch(() => setGhist(null));
  };
  // D1 — one-click: fill the booking form with the returning guest's saved
  // details. Keeps anything the desk already typed if the guest record is blank.
  const prefillGuest = (g) => {
    if (!g) return;
    const keep = (val, cur) => (val != null && val !== "" ? val : cur);
    setForm((f) => ({
      ...f,
      title: keep(g.title, f.title),
      first_name: keep(g.first_name, f.first_name),
      middle_name: keep(g.middle_name, f.middle_name),
      last_name: keep(g.last_name, f.last_name),
      gender: keep(g.gender, f.gender),
      date_of_birth: keep(g.date_of_birth, f.date_of_birth),
      language: keep(g.language, f.language),
      guest_phone: keep(g.guest_phone, f.guest_phone),
      guest_phone2: keep(g.guest_phone2, f.guest_phone2),
      guest_email: keep(g.guest_email, f.guest_email),
      origin: keep(g.origin, f.origin),
      nationality: keep(g.nationality, f.nationality),
      id_type: keep(g.id_type, f.id_type),
      id_number: keep(g.id_number, f.id_number),
    }));
  };
  // Fuller display name for the banner (some old records stored only a first name).
  const guestFullName = (g) => {
    const parts = [g.first_name, g.middle_name, g.last_name].filter(Boolean);
    return parts.length ? parts.join(" ") : (g.guest_name || "");
  };
  // Returning-guest SEARCH (rebook): search past guests by name/phone, pick one
  // to prefill the whole form. Available to every front desk (not gated).
  const [gq, setGq] = useState("");
  const [gResults, setGResults] = useState([]);
  const [gOpen, setGOpen] = useState(false);
  useEffect(() => {
    const s = (gq || "").trim();
    if (s.length < 2) { setGResults([]); return; }
    const h = setTimeout(() => {
      searchGuests(s).then((r) => { setGResults((r && r.results) || []); setGOpen(true); }).catch(() => setGResults([]));
    }, 250);
    return () => clearTimeout(h);
  }, [gq]);
  const pickGuest = (g) => {
    prefillGuest(g);
    setGq(guestFullName(g));
    setGResults([]); setGOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const nameParts = [form.first_name, form.middle_name, form.last_name].map((s) => (s || "").trim()).filter(Boolean);
    let composedName = nameParts.length ? nameParts.join(" ") : (form.guest_name || "").trim();
    // Company-hold booking: the guest is named at check-in, so the booking is
    // held under the company's name.
    if (!composedName && form.company_hold) {
      const heldCoId = form.source === "corporate" ? form.company_account_id : form.perdiem_company_account_id;
      const heldCo = companies.find((co) => String(co.id) === String(heldCoId));
      composedName = (form.company_name || (heldCo && heldCo.name) || "Company hold").trim();
    }
    if (!composedName) { setErr(t("name_req")); return; }
    if (form.check_out_date <= form.check_in_date) { setErr(t("date_req")); return; }
    const body = {
      ...form,
      guest_name: composedName,
      first_name: form.first_name || null,
      middle_name: form.middle_name || null,
      last_name: form.last_name || null,
      gender: form.gender || null,
      title: form.title || null,
      date_of_birth: form.date_of_birth || null,
      language: form.language || null,
      complimentary: !!form.complimentary,
      room_type_id: form.room_type_id ? Number(form.room_type_id) : null,
      room_id: form.room_id ? Number(form.room_id) : null,
      adults: Number(form.adults) || 1,
      children: Number(form.children) || 0,
      rate_per_night: form.rate_per_night === "" ? null : Number(form.rate_per_night),
      rack_rate: form.rack_rate === "" ? null : Number(form.rack_rate),
      residency: form.residency || null,
      fx_currency: form.residency === "foreigner" ? (form.fx_currency || "ETB") : "ETB",
      fx_rate: form.residency === "foreigner" && form.fx_rate !== "" ? Number(form.fx_rate) : null,
      bill_to_company: form.source === "corporate",
      guest_phone: form.guest_phone || null,
      guest_phone2: form.guest_phone2 || null,
      guest_email: form.guest_email || null,
      origin: form.origin || null,
      nationality: form.nationality || null,
      id_type: form.id_type || null,
      id_number: form.id_number || null,
      company_name: form.source === "corporate" ? (form.company_name || null) : null,
      bill_to_tin: form.source === "corporate" ? (form.bill_to_tin || null) : null,
      company_account_id: form.source === "corporate" && form.company_account_id ? Number(form.company_account_id) : null,
      perdiem_company_account_id: form.source !== "corporate" && form.perdiem_company_account_id ? Number(form.perdiem_company_account_id) : null,
      company_hold: !!form.company_hold,
      consent_given: !!form.consent_given,
      cancellation_policy: form.cancellation_policy || null,
      address_country: form.address_country || null,
      region: form.region || null,
      city: form.city || null,
      sub_city: form.sub_city || null,
      woreda: form.woreda || null,
      kebele: form.kebele || null,
      street: form.street || null,
      post_code: form.post_code || null,
    };
    try {
      await createReservation(body);
      setForm(emptyForm());
      setAdding(false);
      load();
    } catch {
      setErr(t("could_not"));
    }
  };

  const doCancel = async (r) => {
    const reason = window.prompt(t("cancel_q"));
    if (reason === null) return;
    try { await cancelReservation(r.id, { reason }); load(); } catch { load(); }
  };
  const doNoShow = async (r) => {
    if (!window.confirm(t("noshow_q"))) return;
    try { await noShowReservation(r.id); load(); } catch { load(); }
  };
  const doReinstate = async (r) => {
    try { await reinstateReservation(r.id); load(); } catch { load(); }
  };
  const linkBtn = (c) => ({ fontSize: 12, background: "none", border: "none", color: c, cursor: "pointer", padding: 0 });
  const openCheckin = (r) => {
    setPanel({ id: r.id, type: "checkin" });
    setPf({ room_id: r.room_id || "", method: "cash", reference: "", amount: String(r.balance_due != null ? r.balance_due : (r.total || "")), guest_name: "" });
    setPanelErr(""); setAvailCheckin(null);
    fetchRoomAvailability(r.check_in_date, r.check_out_date, r.room_type_id || null, r.id).then(setAvailCheckin).catch(() => setAvailCheckin(null));
  };
  const openCheckout = (r) => { setPanel({ id: r.id, type: "checkout" }); setPf({ room_id: r.room_id || "", method: "cash", reference: "", amount: String(r.balance_due || 0) }); setPanelErr(""); };
  const openMove = (r) => {
    setPanel({ id: r.id, type: "move" });
    setMf({ room_id: "", check_out_date: r.check_out_date });
    setPanelErr(""); setAvailCheckin(null);
    fetchRoomAvailability(r.check_in_date, r.check_out_date, null, r.id).then(setAvailCheckin).catch(() => setAvailCheckin(null));
  };
  const openExtend = (r) => {
    setPanel({ id: r.id, type: "extend" });
    setMf({ room_id: r.room_id || "", check_out_date: r.check_out_date });
    setPanelErr("");
  };
  // Assign a room to a booked, room-less reservation without checking it in.
  // Mirrors openMove: same panel/RoomPicker machinery, availability into availCheckin.
  const openAssign = (r) => {
    setPanel({ id: r.id, type: "assign" });
    setMf({ room_id: "", check_out_date: r.check_out_date });
    setPanelErr(""); setAvailCheckin(null);
    fetchRoomAvailability(r.check_in_date, r.check_out_date, r.room_type_id || null, r.id).then(setAvailCheckin).catch(() => setAvailCheckin(null));
  };
  const confirmMove = async () => {
    setPanelErr("");
    if (!mf.room_id) { setPanelErr(t("assign_room_req")); return; }
    try { await moveRoom(panel.id, { room_id: Number(mf.room_id) }); closePanel(); load(); }
    catch (e) {
      const code = e && e.response && e.response.data && e.response.data.detail && e.response.data.detail.code;
      setPanelErr(code === "room_occupied" ? t("room_busy") : code === "room_ooo" ? t("room_ooo_m") : t("could_not"));
    }
  };
  const confirmAssign = async () => {
    setPanelErr("");
    if (!mf.room_id) { setPanelErr(t("assign_room_req")); return; }
    try { await updateReservation(panel.id, { room_id: Number(mf.room_id) }); closePanel(); load(); }
    catch (e) {
      const code = e && e.response && e.response.data && e.response.data.detail && e.response.data.detail.code;
      setPanelErr(code === "room_occupied" ? t("room_busy") : code === "room_ooo" ? t("room_ooo_m") : t("could_not"));
    }
  };
  const confirmExtend = async () => {
    setPanelErr("");
    try { await changeStay(panel.id, { check_out_date: mf.check_out_date }); closePanel(); load(); }
    catch (e) {
      const code = e && e.response && e.response.data && e.response.data.detail && e.response.data.detail.code;
      setPanelErr(code === "room_occupied" ? t("room_busy") : code === "bad_dates" ? t("bad_dates_m") : t("could_not"));
    }
  };

  // Deep-link from the Front Desk board: /front-desk/reservations?focus=<id>
  // scrolls to that booking and, if it still needs check-in, opens the room-
  // assignment panel straight away.
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const [focusDone, setFocusDone] = useState(false);
  useEffect(() => {
    if (!focusId || focusDone || !rows) return;
    const r = rows.find((x) => String(x.id) === String(focusId));
    if (!r) { if (view !== "all") setView("all"); else setFocusDone(true); return; }
    setFocusDone(true);
    if (r.status === "booked") openCheckin(r);
    setTimeout(() => {
      const el = document.getElementById("res-" + r.id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId, focusDone, rows, view]);
  const closePanel = () => { setPanel(null); setPanelErr(""); };
  const confirmCheckin = async () => {
    setPanelErr("");
    try {
      await checkInReservation(panel.id, {
        room_id: pf.room_id ? Number(pf.room_id) : null,
        payment_method: pf.method, payment_reference: pf.reference || null,
        payment_amount: Number(pf.amount) || 0,
        ...(pf.guest_name && pf.guest_name.trim() ? { guest_name: pf.guest_name.trim() } : {}),
      });
      closePanel(); load();
    } catch (e) {
      const code = e && e.response && e.response.data && e.response.data.detail && e.response.data.detail.code;
      setPanelErr(code === "room_occupied" ? t("room_busy") : code === "room_required" ? t("assign_room_req") : t("could_not"));
    }
  };
  const confirmCheckout = async () => {
    setPanelErr("");
    try {
      await checkOutReservation(panel.id, {
        payment_method: pf.method, payment_reference: pf.reference || null,
        payment_amount: Number(pf.amount) || 0,
      });
      closePanel(); load();
    } catch { setPanelErr(t("could_not")); }
  };
  const doDiscount = async (r) => {
    const amtStr = window.prompt(t("disc_amount_q"));
    if (amtStr === null) return;
    const amount = Number(amtStr) || 0;
    const reason = amount > 0 ? (window.prompt(t("disc_reason_q")) || "") : "";
    try {
      await setReservationDiscount(r.id, { amount, reason });
      load();
    } catch (e) {
      const code = e && e.response && e.response.data && e.response.data.detail && e.response.data.detail.code;
      if (code === "pin_required") {
        const pin = window.prompt(t("pin_q"));
        if (!pin) return;
        try { await setReservationDiscount(r.id, { amount, reason, manager_pin: pin }); load(); } catch { load(); }
      } else { load(); }
    }
  };
  const toggleFolio = (r) => { setFolioId((x) => (x === r.id ? null : r.id)); setCf({ description: "", amount: "", kind: "fb" }); };

  // D3 — check-in upsell (add-on: fd_upsell). Load options for a stay and accept
  // an upgrade or extra; the premium posts to the folio and is logged.
  const upsellOn = hasFeature("fd_upsell");
  const [upsellFor, setUpsellFor] = useState(null);
  const [upOpts, setUpOpts] = useState({});
  const [uf, setUf] = useState({ label: "", amount: "", commission: "" });
  const loadUpsell = (r) => fetchUpsellOptions(r.id).then((o) => { setUpOpts(o || {}); setUpsellFor(r.id); setUf({ label: "", amount: "", commission: "" }); }).catch(() => {});
  const acceptUpsell = (r, body) => addUpsell(r.id, body).then(() => { setUpsellFor(null); load(); }).catch(() => {});

  // D6 — post-stay feedback (unified with ratings). Capture overall + staff + comment.
  const feedbackOn = hasFeature("ratings");
  const [fbFor, setFbFor] = useState(null);
  const [ff, setFf] = useState({ overall: "", staff: "", comment: "" });
  const submitFeedback = (r) => {
    if (!ff.overall) return;
    addStayFeedback(r.id, { overall: Number(ff.overall), staff: ff.staff ? Number(ff.staff) : null, comment: ff.comment || null })
      .then(() => { setFbFor(null); setFf({ overall: "", staff: "", comment: "" }); }).catch(() => {});
  };
  // D7 — pre-arrival / online check-in link (add-on: online_checkin). Generate a
  // tokened public link the guest opens to submit ID + preferences before arrival.
  const checkinOn = hasFeature("online_checkin");
  const [linkFor, setLinkFor] = useState(null);
  const [linkUrl, setLinkUrl] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const showPrecheckinLink = (r) => getPrecheckinLink(r.id)
    .then((d) => { setLinkUrl((d && d.url) || ""); setLinkFor(r.id); setLinkCopied(false); }).catch(() => {});
  const copyLink = () => { try { navigator.clipboard.writeText(linkUrl); setLinkCopied(true); } catch { /* ignore */ } };
  const viewIdImage = (r) => getReservationIdImage(r.id).then((d) => {
    if (d && d.id_image) {
      const src = safeImageUrl(d.id_image);
      const w = window.open();
      if (w && src) { w.document.write(`<img src="${escapeHtml(src)}" style="max-width:100%">`); }
    }
  }).catch(() => {});

  // D8 — guest messaging over Telegram (add-on: guest_messaging).
  const messagingOn = hasFeature("guest_messaging");
  const [msgCfg, setMsgCfg] = useState(null);      // {configured, username}
  const [setupTok, setSetupTok] = useState("");
  const [setupBusy, setSetupBusy] = useState(false);
  const [msgFor, setMsgFor] = useState(null);
  const [optIn, setOptIn] = useState(null);        // {ready, linked, url}
  const [msgText, setMsgText] = useState("");
  const [msgLog, setMsgLog] = useState([]);
  const [msgFlash, setMsgFlash] = useState("");
  useEffect(() => { if (messagingOn) { getMessagingStatus().then(setMsgCfg).catch(() => setMsgCfg({ configured: false })); } }, [messagingOn]);
  const doSetup = () => {
    if (!setupTok.trim()) return;
    setSetupBusy(true);
    setupMessaging(setupTok.trim())
      .then((d) => { setMsgCfg({ configured: true, username: d.username }); setSetupTok(""); setMsgFlash(d.webhook_ok ? "" : "Saved, but webhook not set — check the bot token."); })
      .catch((e) => setMsgFlash((e && e.response && e.response.data && e.response.data.detail) || "Could not validate the bot token."))
      .finally(() => setSetupBusy(false));
  };
  const openMsg = (r) => {
    setMsgFor(r.id); setMsgText(""); setMsgFlash(""); setOptIn(null); setMsgLog([]);
    if (!r.messaging_linked) getOptInLink(r.id).then(setOptIn).catch(() => {});
    fetchGuestMessages(r.id).then(setMsgLog).catch(() => {});
  };
  // D8 — guest connect QR shown at check-in. Reuses the same opt-in link the
  // "Message" feature produces + the same inline QR data-URL as table QR codes.
  const [connectFor, setConnectFor] = useState(null);
  const [connect, setConnect] = useState(null);   // {ready, linked, url, qr_code_url}
  const openConnect = (r) => {
    setConnectFor(r.id); setConnect(null);
    getOptInLink(r.id).then(setConnect).catch(() => {});
  };
  const sendCheckin = (r) => sendPrecheckinMessage(r.id).then((d) => {
    setMsgFlash(d.ok ? "Check-in link sent." : (d.detail || d.error || "Could not send."));
    fetchGuestMessages(r.id).then(setMsgLog).catch(() => {});
  }).catch(() => setMsgFlash("Could not send."));
  const sendMsg = (r) => {
    if (!msgText.trim()) return;
    sendGuestMessage(r.id, { text: msgText.trim(), kind: "note" }).then((d) => {
      setMsgFlash(d.ok ? "Message sent." : (d.detail || d.error || "Could not send."));
      if (d.ok) setMsgText("");
      fetchGuestMessages(r.id).then(setMsgLog).catch(() => {});
    }).catch(() => setMsgFlash("Could not send."));
  };
  const MSG_TEMPLATES = [
    "Checkout is at 12:00 noon. Let us know if you'd like a late checkout.",
    "Breakfast is served 7:00–10:00 in the restaurant. Enjoy your stay!",
    "Welcome! If you need anything at all, just reply here and the front desk will help.",
  ];

  const submitCharge = async (r) => {
    if (!cf.description.trim() || !(Number(cf.amount) > 0)) return;
    try {
      await addReservationCharge(r.id, { description: cf.description.trim(), amount: Number(cf.amount), kind: cf.kind, source: cf.source });
      setCf({ description: "", amount: "", kind: "fb", source: "other" });
      load();
    } catch { load(); }
  };
  const delCharge = async (r, cid) => { try { await deleteReservationCharge(r.id, cid); load(); } catch { load(); } };
  // Guest approval of a folio charge (money / consent).
  const markSigned = async (r, cid) => { try { await markChargeSigned(r.id, cid); } catch { /* ignore */ } load(); };
  const reqApproval = async (r, cid) => {
    try {
      const d = await requestChargeApproval(r.id, cid);
      if (d && d.sent === false) window.alert(t(d.reason === "not_connected" ? "not_connected_hint" : "approval_failed"));
      else window.alert(t("approval_sent"));
    } catch { /* ignore */ }
    load();
  };
  const doTransfer = async (r) => {
    if (!xfer || !xfer.target) return;
    try { await transferReservationCharge(r.id, xfer.cid, Number(xfer.target)); } catch { /* ignore */ }
    setXfer(null); load();
  };
  const loadSvc = (rid) => fetchGuestServices(rid).then(setSvcList).catch(() => setSvcList([]));
  const toggleServices = (r) => {
    const opening = svcId !== r.id;
    setSvcId(opening ? r.id : null);
    setSf({ kind: "request", detail: "", due_at: "" });
    if (opening) loadSvc(r.id);
  };
  const addSvc = async (r) => {
    if (!sf.detail.trim() && sf.kind !== "wakeup") return;
    const body = { kind: sf.kind, detail: sf.detail.trim() || null, due_at: sf.due_at ? new Date(sf.due_at).toISOString() : null };
    try { await addGuestService(r.id, body); setSf({ kind: "request", detail: "", due_at: "" }); loadSvc(r.id); } catch { loadSvc(r.id); }
  };
  const doneSvc = async (r, s) => { try { await updateGuestService(r.id, s.id, { status: s.status === "done" ? "open" : "done" }); loadSvc(r.id); } catch { loadSvc(r.id); } };
  const delSvc = async (r, sid) => { try { await deleteGuestService(r.id, sid); loadSvc(r.id); } catch { loadSvc(r.id); } };
  const printReceipt = async (r) => {
    let d;
    try { d = await fetchReservationReceipt(r.id); } catch { return; }
    const rc = d.hotel.currency || cur;
    const m = (v) => formatPrice(v || 0, rc);
    const h = d.hotel || {};
    const cst = d.customer || {};
    const rv = d.reservation || {};
    const L = {
      billto: { en: "Bill to", am: "ለ", om: "Kan kaffalu" },
      subtotal: { en: "Subtotal", am: "ንዑስ ድምር", om: "Ida'ama xiqqaa" },
      paid: { en: "Paid", am: "የተከፈለ", om: "Kaffalame" },
    };
    const tr2 = (k) => (L[k] && (L[k][lang] || L[k].en)) || k;
    const hasTax = (d.tax_amount || d.vat_amount || 0) > 0;
    const idLine = [h.tin ? `TIN: ${h.tin}` : "", h.vat_reg_no ? `VAT: ${h.vat_reg_no}` : ""].filter(Boolean).join(" · ");
    const invNo = d.invoice_no ? `No. ${d.invoice_no}` : "";
    // Mirror the on-screen folio grouping on the printed invoice: group lines by
    // revenue point (accommodation = room line; then F&B / Wellness / Room service
    // / Other), each with a subtotal. The service charge line stays a tail line.
    // Purely presentational — the grand total / tax / balance below are unchanged.
    const catLabels = { accommodation: t("cat_accommodation"), fnb: t("cat_fnb"), wellness: t("cat_wellness"), roomservice: t("cat_roomservice"), other: t("cat_other") };
    const catOfLine = (l) => (l.source === "accommodation" ? "accommodation" : (["fnb", "wellness", "roomservice"].includes(l.source) ? l.source : "other"));
    const allLines = d.lines || [];
    const groupLines = allLines.filter((l) => l.source !== "service_charge");
    const svcLines = allLines.filter((l) => l.source === "service_charge");
    const hdrCss = "font-weight:700;font-size:10.5px;text-transform:uppercase;letter-spacing:.4px;color:#555;padding-top:6px";
    const subCss = "font-size:11px;color:#666";
    let rows = "";
    ["accommodation", "fnb", "wellness", "roomservice", "other"].forEach((g) => {
      const items = groupLines.filter((l) => catOfLine(l) === g);
      if (!items.length) return;
      rows += `<tr><td colspan="2" style="${hdrCss}">${catLabels[g]}</td></tr>`;
      items.forEach((l) => { rows += `<tr><td>${l.description}</td><td class="r">${m(l.amount)}</td></tr>`; });
      const sub = items.reduce((s, l) => s + (l.amount || 0), 0);
      rows += `<tr><td style="${subCss}">${tr2("subtotal")}</td><td class="r" style="${subCss}">${m(sub)}</td></tr>`;
    });
    svcLines.forEach((l) => { rows += `<tr><td>${l.description}</td><td class="r">${m(l.amount)}</td></tr>`; });
    const discLine = d.discount > 0 ? `<tr><td>${t("discount_l")}</td><td class="r">-${m(d.discount)}</td></tr>` : "";
    const netLine = hasTax ? `<tr><td>${tr2("subtotal")}</td><td class="r">${m(d.subtotal_net)}</td></tr>` : "";
    const taxLine = hasTax ? `<tr><td>${d.tax_label || "Tax"}</td><td class="r">${m(d.tax_amount || d.vat_amount)}</td></tr>` : "";
    const custCompany = cst.company ? `<div class="muted">${cst.company}</div>` : "";
    const custTin = cst.tin ? `<div class="muted">TIN: ${cst.tin}</div>` : "";
    // Post-checkout Google review QR: printed on the folio when the venue has a
    // review link configured (direct Google QR, or the gate-aware review page).
    // If none is configured we print nothing extra.
    let reviewBlock = "";
    try {
      const rl = await fetchReviewLink(r.id);
      if (rl && rl.qr_code_url) {
        reviewBlock = `<div class="hr"></div><div class="review">
          <img src="${rl.qr_code_url}" alt="Review QR" style="width:120px;height:120px" />
          <div class="muted">${t("review_scan")}</div></div>`;
      }
    } catch (e) { /* ignore — never block the folio print on the review link */ }
    const html = `<html><head><meta charset="utf-8"><title>${h.legal_name || h.name || ""} — ${d.doc_title || "Invoice"}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;max-width:360px;margin:0 auto;padding:16px;color:#111}
      h2{margin:0;font-size:17px}.muted{color:#666;font-size:11.5px}
      .head{text-align:center}.title{text-align:center;font-weight:700;font-size:14px;letter-spacing:.5px;margin:8px 0 2px}
      table{width:100%;border-collapse:collapse;margin-top:6px;font-size:12.5px}td{padding:3px 0;vertical-align:top}
      .r{text-align:right}.tot td{border-top:1px solid #999;font-weight:700;padding-top:6px}
      .hr{border-top:1px dashed #999;margin:8px 0}
      .review{text-align:center;margin-top:6px}.review img{display:block;margin:0 auto 4px}</style></head><body>
      <div class="head">
        <h2>${h.legal_name || h.name || ""}</h2>
        ${h.address ? `<div class="muted">${h.address}</div>` : ""}
        ${h.phone ? `<div class="muted">${h.phone}</div>` : ""}
        ${idLine ? `<div class="muted">${idLine}</div>` : ""}
      </div>
      <div class="title">${d.doc_title || "Invoice"}</div>
      <div class="muted head">${invNo}${d.issued_at ? (invNo ? " · " : "") + new Date(d.issued_at).toLocaleString() : ""}</div>
      <div class="hr"></div>
      <div class="muted"><b>${tr2("billto")}:</b> ${cst.name || rv.guest_name || ""}</div>
      ${custCompany}${custTin}
      <div class="muted">${rv.check_in_date || ""} → ${rv.check_out_date || ""}${rv.room_name ? " · " + t("room_line") + " " + rv.room_name : ""}</div>
      <table>${rows}${discLine}${netLine}${taxLine}
        <tr class="tot"><td>${t("grand_total")}</td><td class="r">${m(d.grand_total)}</td></tr></table>
      <div class="hr"></div>
      <table><tr><td>${tr2("paid")}</td><td class="r">${m(d.paid)}</td></tr>
        ${d.balance_due > 0 ? `<tr><td>${t("balance")}</td><td class="r">${m(d.balance_due)}</td></tr>` : ""}</table>
      <div class="muted" style="margin-top:8px">${d.payment_method || ""}${d.payment_reference ? " · " + d.payment_reference : ""}</div>
      ${d.fiscal ? `<div class="hr"></div>
        <div class="muted head" style="margin-top:6px;font-weight:700">E-invoice${d.fiscal.provider === "mock" ? " (dry-run)" : ""}</div>
        ${d.fiscal.qr_image ? `<div style="text-align:center;margin-top:4px"><img src="${d.fiscal.qr_image}" style="width:120px;height:120px"/></div>` : ""}
        <div class="muted head" style="font-size:10px;word-break:break-all">${d.fiscal.irn || ""}</div>` : ""}
      ${reviewBlock}
    </body></html>`;
    const w = window.open("", "_blank", "width=400,height=680");
    if (!w) return;
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* ignore */ } }, 300);
  };

  // Room-charge voucher — a CONTROL document the guest signs, printed at the
  // FRONT DESK for an outlet charge line (F&B / wellness / room service) that
  // landed on the folio. NOT a payment receipt. Mirrors the thermal print in
  // printReceipt above and the voucher markup from the cashier's tabs. Hotel
  // name comes from the same receipt payload printReceipt uses.
  const printVoucher = async (r, c) => {
    let h = {};
    try { const d = await fetchReservationReceipt(r.id); h = (d && d.hotel) || {}; } catch (e) { /* ignore */ }
    const hotel = h.legal_name || h.name || "";
    const rc = h.currency || cur;
    const m = (v) => formatPrice(v || 0, rc);
    const room = r.room_name || "";
    const gname = r.guest_name || "";
    const w = window.open("", "_blank", "width=380,height=640");
    if (!w) return;
    w.document.write(`<html><head><meta charset="utf-8"><title>${escapeHtml(hotel)} — ${escapeHtml(t("v_title"))}</title><style>
      body{font-family:Arial,Helvetica,sans-serif;max-width:360px;margin:0 auto;padding:16px;color:#111}
      h2{margin:0;font-size:17px}.muted{color:#666;font-size:11.5px}
      .head{text-align:center}
      .title{text-align:center;font-weight:700;font-size:15px;letter-spacing:.6px;text-transform:uppercase;margin:8px 0 0}
      .sub{text-align:center;font-size:11px;color:#666;margin-bottom:2px}
      table{width:100%;border-collapse:collapse;margin-top:6px;font-size:12.5px}td{padding:3px 0;vertical-align:top}
      .r{text-align:right}.tot td{border-top:1px solid #999;font-weight:700;padding-top:6px}
      .hr{border-top:1px dashed #999;margin:8px 0}
      .sign{margin-top:22px;font-size:12.5px}
      .note{margin-top:14px;font-size:11px;text-align:center;color:#555}</style></head><body>
      <div class="head"><h2>${escapeHtml(hotel)}</h2></div>
      <div class="title">${escapeHtml(t("v_title"))}</div>
      <div class="sub">${escapeHtml(t("v_not_receipt"))}</div>
      <div class="muted head">${new Date().toLocaleString()}</div>
      <div class="hr"></div>
      <div class="muted"><b>${t("v_room")}:</b> ${escapeHtml(room || "—")}</div>
      <div class="muted"><b>${t("v_guest")}:</b> ${escapeHtml(gname || "—")}</div>
      <div class="hr"></div>
      <table>
        <tr><td>${escapeHtml(c.description || "")}</td><td class="r">${m(c.amount)}</td></tr>
        <tr class="tot"><td>${t("v_total")}</td><td class="r">${m(c.amount)}</td></tr>
      </table>
      <div class="sign">${t("v_sign")}: ______________________</div>
      <div class="note">${escapeHtml(t("v_settle"))}</div>
    </body></html>`);
    w.document.close(); w.focus(); setTimeout(() => { try { w.print(); } catch (e) { /* ignore */ } }, 250);
  };

  // Feature A — correct/complete a guest's details while they're active (booked
  // or in-house). Prefill from the reservation; structured name if it carries
  // first/middle/last, otherwise a single guest_name field.
  const openEdit = (r) => {
    const structured = (r.first_name != null || r.middle_name != null || r.last_name != null);
    setEditing(r.id);
    setEditErr("");
    setEf({
      structured,
      title: r.title || "",
      guest_name: r.guest_name || "",
      first_name: r.first_name || "",
      middle_name: r.middle_name || "",
      last_name: r.last_name || "",
      guest_phone: r.guest_phone || "",
      nationality: r.nationality || "",
      id_type: r.id_type || "",
      id_number: r.id_number || "",
      origin: r.origin || "",
      bill_to_tin: r.bill_to_tin || "",
      company_name: r.company_name || "",
    });
  };
  const saveEdit = async (r) => {
    setEditErr("");
    const patch = {};
    if (ef.structured) {
      const fn = (ef.first_name || "").trim(), mn = (ef.middle_name || "").trim(), ln = (ef.last_name || "").trim();
      const composed = [fn, mn, ln].filter(Boolean).join(" ");
      if (!composed) { setEditErr(t("name_req")); return; }
      patch.first_name = fn || null;
      patch.middle_name = mn || null;
      patch.last_name = ln || null;
      patch.guest_name = composed;
    } else {
      const gn = (ef.guest_name || "").trim();
      if (!gn) { setEditErr(t("name_req")); return; }
      patch.guest_name = gn;
    }
    patch.title = ef.title || null;
    patch.guest_phone = ef.guest_phone || null;
    patch.nationality = ef.nationality || null;
    patch.id_type = ef.id_type || null;
    patch.id_number = ef.id_number || null;
    patch.origin = ef.origin || null;
    patch.bill_to_tin = ef.bill_to_tin || null;
    patch.company_name = ef.company_name || null;
    try { await updateReservation(r.id, patch); setEditing(null); load(); }
    catch { setEditErr(t("could_not")); }
  };

  // Feature B — confirm/persist the customer TIN (and optional bill-to name)
  // before printing the invoice. Leaving them blank is allowed.
  const openTin = (r) => { setTinFor(r.id); setTf({ bill_to_tin: r.bill_to_tin || "", company_name: r.company_name || "" }); };
  const doPrintBill = async (r) => {
    const newTin = (tf.bill_to_tin || "").trim();
    const newName = (tf.company_name || "").trim();
    const patch = {};
    if (newTin !== (r.bill_to_tin || "")) patch.bill_to_tin = newTin || null;
    if (newName !== (r.company_name || "")) patch.company_name = newName || null;
    try { if (Object.keys(patch).length) await updateReservation(r.id, patch); } catch { /* still print with what's saved */ }
    setTinFor(null);
    printReceipt(r);
  };

  const inp = { padding: 8, borderRadius: 8, border: "1px solid #d8dee2", boxSizing: "border-box" };
  const rate = types.find((x) => String(x.id) === String(form.room_type_id));
  const isEth = (form.address_country || "").trim().toLowerCase().startsWith("eth");
  const roomChoices = rooms.filter((rm) => !form.room_type_id || String(rm.room_type_id) === String(form.room_type_id));

  // Search the visible list by room number or guest name (helps when a big
  // hotel has a long arrivals/in-house list at check-in / check-out).
  const _qq = q.trim().toLowerCase();
  const shownRows = !_qq || rows == null ? rows : rows.filter((r) =>
    (r.guest_name || "").toLowerCase().includes(_qq) ||
    (r.room_name || "").toLowerCase().includes(_qq) ||
    String(r.id).includes(_qq)
  );

  return (
    <div style={{ maxWidth: 1040, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
        <div>
          <h1 style={{ marginBottom: 2 }}>{t("title")}</h1>
          <div className="muted" style={{ fontSize: 14 }}>{t("subtitle")}</div>
        </div>
        {canEdit && <button className="btn primary small" onClick={() => setAdding((v) => !v)}>{t("new_booking")}</button>}
      </div>

      {messagingOn && msgCfg && !msgCfg.configured && canEdit && (
        <div style={{ marginTop: 12, background: "#EAF6FC", border: "1px solid #cbe6f5", borderRadius: 10, padding: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: "#0077b3" }}>✈ {t("tg_setup_title")}</div>
          <div className="muted" style={{ fontSize: 12.5, margin: "4px 0 8px" }}>{t("tg_setup_help")}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <input style={{ ...inp, flex: 1, minWidth: 240 }} placeholder={t("tg_token_ph")} value={setupTok} onChange={(e) => setSetupTok(e.target.value)} />
            <button className="btn primary small" disabled={setupBusy || !setupTok.trim()} onClick={doSetup}>{setupBusy ? "…" : t("tg_connect")}</button>
          </div>
          {msgFlash && <div style={{ fontSize: 12.5, color: "#A32D2D", marginTop: 6 }}>{msgFlash}</div>}
        </div>
      )}
      {messagingOn && msgCfg && msgCfg.configured && msgCfg.username && canEdit && (
        <div className="muted" style={{ fontSize: 12, marginTop: 8, color: "#0077b3" }}>✈ {t("tg_connected")} @{msgCfg.username}</div>
      )}

      {/* view tabs */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 14 }}>
        {VIEWS.map((v) => (
          <button key={v} onClick={() => setView(v)}
            style={{
              fontSize: 13, padding: "6px 14px", borderRadius: 20, cursor: "pointer",
              border: view === v ? "1.5px solid #0F6E56" : "1px solid #e2e6ea",
              background: view === v ? "#E1F5EE" : "#fff",
              color: view === v ? "#0F6E56" : "#5b6472", fontWeight: view === v ? 700 : 500,
            }}>
            {t("v_" + v)}
          </button>
        ))}
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={lang === "am" ? "ክፍል ወይም ስም ፈልግ…" : lang === "om" ? "Kutaa ykn maqaa barbaadi…" : "Search room or guest name…"}
          style={{ ...inp, marginLeft: "auto", minWidth: 220, flex: "0 1 260px" }}
        />
      </div>

      {/* booking form */}
      {adding && canEdit && (
        <form onSubmit={submit} style={{ ...CARD, padding: 16, marginTop: 14 }}>
          {/* Returning-guest search — pick a past guest to prefill (rebook). */}
          <div style={{ marginBottom: 10, position: "relative", maxWidth: 440 }}>
            <input style={{ ...inp, width: "100%" }} value={gq}
              onChange={(e) => setGq(e.target.value)} onFocus={() => gResults.length && setGOpen(true)}
              placeholder={lang === "am" ? "ተመላሽ እንግዳ? በስም ወይም ስልክ ይፈልጉ" : lang === "om" ? "Keessummaa deebi'e? Maqaa ykn bilbilaan barbaadi" : "Returning guest? Search name or phone to rebook"} />
            {gOpen && gResults.length > 0 && (
              <>
                <div onClick={() => setGOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
                <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 31, background: "#fff", border: "1px solid #d8dee2", borderRadius: 8, boxShadow: "0 6px 20px rgba(6,59,72,0.14)", marginTop: 4, maxHeight: 280, overflowY: "auto" }}>
                  {gResults.map((g, i) => (
                    <button type="button" key={i} onClick={() => pickGuest(g)}
                      style={{ display: "block", width: "100%", textAlign: "left", background: "none", border: "none", borderBottom: "1px solid #f0f2f2", padding: "8px 10px", cursor: "pointer", fontSize: 13.5 }}>
                      <b>{guestFullName(g)}</b>{g.guest_phone ? ` · ${g.guest_phone}` : ""}{g.nationality ? ` · ${g.nationality}` : ""}
                      {g.last_stay ? <span className="muted" style={{ fontSize: 12 }}>{`  · ${g.last_stay}`}</span> : null}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
          {recogOn && ghist && (
            <div style={{ background: "#EEF6FF", border: "1px solid #cfe4fb", borderRadius: 10, padding: "8px 12px", marginBottom: 10, fontSize: 13.5, color: "#0C447C", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ flex: 1, minWidth: 200 }}>
                <b>★ {t("returning")}</b> — {guestFullName(ghist)}
                {ghist.guest_phone ? ` · ${ghist.guest_phone}` : ""} · {ghist.stay_count} {t("stays")}
                {ghist.last_room_type ? ` · ${t("prefers")} ${ghist.last_room_type}` : ""}
                {ghist.pref_meal_plan ? ` (${String(ghist.pref_meal_plan).toUpperCase()})` : ""}
                {ghist.open_balance > 0 ? ` · ${t("owes")} ${formatPrice(ghist.open_balance, getCurrency() || "ETB")}` : ""}
              </span>
              <button type="button" className="btn primary small" onClick={() => prefillGuest(ghist)}>{t("use_details")}</button>
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
            <select style={{ ...inp, maxWidth: 110 }} value={form.title} onChange={(e) => set("title", e.target.value)}>
              <option value="">{lang === "am" ? "ማዕረግ" : lang === "om" ? "Sadarkaa" : "Title"}</option>
              <option value="Mr">Mr</option>
              <option value="Mrs">Mrs</option>
              <option value="Ms">Ms</option>
              <option value="Ato">Ato</option>
              <option value="W/ro">W/ro</option>
              <option value="W/rt">W/rt</option>
              <option value="Dr">Dr</option>
              <option value="Prof">Prof</option>
            </select>
            <input style={inp} placeholder={t("fname") + " *"} value={form.first_name} onChange={(e) => set("first_name", e.target.value)} autoFocus />
            <input style={inp} placeholder={t("mname")} value={form.middle_name} onChange={(e) => set("middle_name", e.target.value)} />
            <input style={inp} placeholder={t("lname")} value={form.last_name} onChange={(e) => set("last_name", e.target.value)} />
            <select style={inp} value={form.gender} onChange={(e) => set("gender", e.target.value)}>
              <option value="">{t("gender")}</option>
              <option value="male">{t("male")}</option>
              <option value="female">{t("female")}</option>
              <option value="other">{t("g_other")}</option>
            </select>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{lang === "am" ? "የልደት ቀን" : lang === "om" ? "Guyyaa dhalootaa" : "Date of birth"}
              <input type="date" style={{ ...inp, width: "100%", marginTop: 3 }} value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} />
            </label>
            <input style={inp} placeholder={t("language")} value={form.language} onChange={(e) => set("language", e.target.value)} />
            <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#5f6b78", padding: "0 4px" }}>
              <input type="checkbox" checked={form.complimentary} onChange={(e) => set("complimentary", e.target.checked)} /> {t("complimentary")}
            </label>
            <input style={inp} placeholder={t("phone")} value={form.guest_phone} onChange={(e) => set("guest_phone", e.target.value)} onBlur={lookupGuest} />
            <input style={inp} placeholder={lang === "am" ? "ስልክ 2" : lang === "om" ? "Bilbila 2" : "Phone 2"} value={form.guest_phone2} onChange={(e) => set("guest_phone2", e.target.value)} />
            <input style={inp} placeholder={t("email")} value={form.guest_email} onChange={(e) => set("guest_email", e.target.value)} onBlur={lookupGuest} />
            <input style={inp} placeholder={t("origin")} value={form.origin} onChange={(e) => set("origin", e.target.value)} />
            <input style={inp} placeholder={t("nationality")} value={form.nationality} onChange={(e) => set("nationality", e.target.value)} />
            <select style={inp} value={form.id_type} onChange={(e) => set("id_type", e.target.value)}>
              <option value="">{t("id_type")}: {t("id_none")}</option>
              <option value="kebele">{t("id_kebele")}</option>
              <option value="fayda">{t("id_fayda")}</option>
              <option value="passport">{t("id_passport")}</option>
              <option value="other">{t("id_other")}</option>
            </select>
            <input style={inp} placeholder={t("id_number")} value={form.id_number} onChange={(e) => set("id_number", e.target.value)} onBlur={lookupGuest} />
            <select style={inp} value={form.source} onChange={(e) => set("source", e.target.value)}>
              <option value="walk_in">{t("src_walk_in")}</option>
              <option value="phone">{t("src_phone")}</option>
              <option value="corporate">{t("src_corporate")}</option>
            </select>
            <select style={inp} value={form.market} onChange={(e) => set("market", e.target.value)}>
              <option value="">{lang === "am" ? "የገበያ ክፍል" : lang === "om" ? "Kutaa gabaa" : "Market segment"}</option>
              <option value="corporate">{lang === "am" ? "ድርጅት" : lang === "om" ? "Dhaabbata" : "Corporate"}</option>
              <option value="leisure">{lang === "am" ? "መዝናኛ" : lang === "om" ? "Bashannana" : "Leisure"}</option>
              <option value="group">{lang === "am" ? "ቡድን" : lang === "om" ? "Garee" : "Group"}</option>
              <option value="government">{lang === "am" ? "መንግስት" : lang === "om" ? "Mootummaa" : "Government"}</option>
              <option value="other">{lang === "am" ? "ሌላ" : lang === "om" ? "Kan biraa" : "Other"}</option>
            </select>
            <input style={inp} placeholder={lang === "am" ? "ኤጀንት (አማራጭ)" : lang === "om" ? "Ejensii (filannoo)" : "Agent (optional)"} value={form.agent} onChange={(e) => set("agent", e.target.value)} />
            {form.source === "corporate" && companies.length > 0 && (
              <select style={inp} value={form.company_account_id} onChange={(e) => set("company_account_id", e.target.value)}>
                <option value="">{lang === "am" ? "የድርጅት ሂሳብ (አማራጭ)" : lang === "om" ? "Herrega dhaabbataa (filannoo)" : "Company account (optional)"}</option>
                {companies.map((co) => <option key={co.id} value={co.id}>{co.name}</option>)}
              </select>
            )}
            {form.source === "corporate" && (
              <input style={inp} placeholder={t("company")} value={form.company_name} onChange={(e) => set("company_name", e.target.value)} />
            )}
            {form.source === "corporate" && (
              <input style={inp} placeholder={t("company_tin")} value={form.bill_to_tin} onChange={(e) => set("bill_to_tin", e.target.value)} />
            )}
            {form.source !== "corporate" && companies.length > 0 && (
              <select style={inp} value={form.perdiem_company_account_id} onChange={(e) => set("perdiem_company_account_id", e.target.value)}>
                <option value="">{lang === "am" ? "ፐር ዲም — ደረሰኝ ለድርጅት (አማራጭ)" : lang === "om" ? "Per-diem — nagahee dhaabbataaf (filannoo)" : "Per-diem — receipt to company (optional)"}</option>
                {companies.map((co) => <option key={co.id} value={co.id}>{co.name}</option>)}
              </select>
            )}
            {(form.source === "corporate" ? form.company_account_id : form.perdiem_company_account_id) && (
              <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#5b6472" }}>
                <input type="checkbox" checked={!!form.company_hold} onChange={(e) => set("company_hold", e.target.checked)} />
                {lang === "am" ? "በድርጅት ስም ያዝ (እንግዳ በመግቢያ)" : lang === "om" ? "Maqaa dhaabbataan qabi (keessummaa seensarratti)" : "Hold in company name (name guest at check-in)"}
              </label>
            )}
            <label style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: 13, color: "#5b6472" }}>
              <input type="checkbox" checked={!!form.consent_given} onChange={(e) => set("consent_given", e.target.checked)} />
              {lang === "am" ? "የግላዊነት ማስታወቂያ ለእንግዳው ተሰጥቷል (መረጃ ለአገልግሎትና ለሕጋዊ ሪፖርት ይውላል) — አዋጅ 1321/2024" : lang === "om" ? "Beeksisni iccitii keessummaaf kennameera (daataan tajaajilaa fi gabaasa seeraatiif ola) — Labsii 1321/2024" : "Privacy notice given to guest (data used for service & legal reporting) — Proclamation 1321/2024"}
            </label>
            <select style={inp} value={form.room_type_id} onChange={(e) => set("room_type_id", e.target.value)}>
              <option value="">{t("pick_type")}</option>
              {types.map((tp) => <option key={tp.id} value={tp.id}>{tp.name}{tp.base_rate != null ? ` — ${formatPrice(tp.base_rate, cur)}` : ""}</option>)}
            </select>
            <select style={inp} value={form.occupancy} onChange={(e) => set("occupancy", e.target.value)}>
              {OCC_OPTS.map((o) => <option key={o.v} value={o.v}>{optLabel(o)}</option>)}
            </select>
            <select style={inp} value={form.meal_plan} onChange={(e) => set("meal_plan", e.target.value)}>
              {MEAL_OPTS.map((o) => <option key={o.v} value={o.v}>{optLabel(o)}</option>)}
            </select>
            <input style={inp} type="number" min="0"
              placeholder={lang === "am" ? "የሌሊት ዋጋ (ራስ-ገዝ)" : lang === "om" ? "Gatii halkanii (ofumaa)" : "Nightly rate (auto — edit to override)"}
              value={form.rate_per_night} onChange={(e) => set("rate_per_night", e.target.value)} />
            <select style={inp} value={form.residency} onChange={(e) => set("residency", e.target.value)}>
              <option value="local">{lang === "am" ? "የአገር ውስጥ" : lang === "om" ? "Biyya keessaa" : "Local"}</option>
              <option value="foreigner">{lang === "am" ? "የውጭ ዜጋ" : lang === "om" ? "Alagaa" : "Foreigner"}</option>
            </select>
            {form.residency === "foreigner" && (
              <>
                <input style={{ ...inp, width: 140 }} list="fx-currency-list"
                  placeholder={lang === "am" ? "ገንዘብ (ፈልግ)" : lang === "om" ? "Sharafa (barbaadi)" : "Currency (search)"}
                  value={form.fx_currency} onChange={(e) => set("fx_currency", e.target.value.toUpperCase())} />
                <datalist id="fx-currency-list">
                  {FX_CURRENCIES.map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                </datalist>
              </>
            )}
            {form.residency === "foreigner" && (
              <input style={{ ...inp, width: 120 }} type="number" min="0" step="0.0001" placeholder={lang === "am" ? "የምንዛሪ ተመን" : lang === "om" ? "Sarara jijjiirraa" : "FX rate → ETB"} value={form.fx_rate} onChange={(e) => set("fx_rate", e.target.value)} />
            )}
            <RoomPicker t={t} value={form.room_id} onChange={(id) => set("room_id", id)}
              rooms={availBooking || roomChoices.map((rm) => ({ ...rm, available: true, conflict: null }))} />
            {rooms.length === 0 && (
              <div style={{ fontSize: 12, color: "#8A5A0B", marginTop: 4, gridColumn: "1 / -1" }}>
                {lang === "am" ? "እስካሁን ክፍል የለም — በፊት ዴስክ → ክፍሎች ውስጥ ይጨምሩ።" : lang === "om" ? "Kutaan hin jiru — Fuula Duraa → Kutaalee jalatti dabali." : "No rooms set up yet — add them under Front Desk → Rooms."}
              </div>
            )}
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("check_in")}
              <input type="date" style={{ ...inp, width: "100%", marginTop: 3 }} value={form.check_in_date} onChange={(e) => set("check_in_date", e.target.value)} />
            </label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("check_out")}
              <input type="date" style={{ ...inp, width: "100%", marginTop: 3 }} value={form.check_out_date} onChange={(e) => set("check_out_date", e.target.value)} />
            </label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("adults")}
              <input type="number" min="1" style={{ ...inp, width: "100%", marginTop: 3 }} value={form.adults} onChange={(e) => set("adults", e.target.value)} />
            </label>
            <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("children")}
              <input type="number" min="0" style={{ ...inp, width: "100%", marginTop: 3 }} value={form.children} onChange={(e) => set("children", e.target.value)} />
            </label>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14, marginTop: 10 }}>
            <select style={inp} value={form.hold_type} onChange={(e) => set("hold_type", e.target.value)}>
              <option value="guaranteed">{lang === "am" ? "የተረጋገጠ" : lang === "om" ? "Mirkanaa'e" : "Guaranteed"}</option>
              <option value="hold_6pm">{lang === "am" ? "6PM ማቆያ" : lang === "om" ? "Eeggannoo 6PM" : "6PM hold"}</option>
            </select>
            <label style={{ fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
              <input type="checkbox" checked={form.waitlist} onChange={(e) => set("waitlist", e.target.checked)} /> {lang === "am" ? "የመጠባበቂያ ዝርዝር" : lang === "om" ? "Tarree eeggannoo" : "Waiting list"}
            </label>
            <button type="button" className="btn secondary small" onClick={() => setShowMore((v) => !v)}>
              {showMore
                ? (lang === "am" ? "ያነሱ ዝርዝሮች" : lang === "om" ? "Xiqqeessi" : "Fewer details")
                : (lang === "am" ? "ተጨማሪ ዝርዝሮች (አድራሻ)" : lang === "om" ? "Bal'ina dabalataa (teessoo)" : "More details (address)")}
            </button>
          </div>

          {/* address (optional) — moved out of the default view; captured here or at check-in */}
          {showMore && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#5f6b78", marginBottom: 6 }}>{t("addr_title")}</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                <select style={inp} value={isEth ? "eth" : "other"} onChange={(e) => set("address_country", e.target.value === "eth" ? "Ethiopia" : "")}>
                  <option value="eth">{t("c_eth")}</option>
                  <option value="other">{t("c_other")}</option>
                </select>
                {isEth ? (
                  <>
                    <input style={inp} placeholder={t("region")} value={form.region} onChange={(e) => set("region", e.target.value)} />
                    <input style={inp} placeholder={t("city")} value={form.city} onChange={(e) => set("city", e.target.value)} />
                    <input style={inp} placeholder={lang === "am" ? "ክፍለ ከተማ" : lang === "om" ? "Kutaa magaalaa" : "Sub-city"} value={form.sub_city} onChange={(e) => set("sub_city", e.target.value)} />
                    <input style={inp} placeholder={t("woreda")} value={form.woreda} onChange={(e) => set("woreda", e.target.value)} />
                    <input style={inp} placeholder={t("kebele")} value={form.kebele} onChange={(e) => set("kebele", e.target.value)} />
                  </>
                ) : (
                  <>
                    <input style={inp} placeholder={t("country")} value={form.address_country} onChange={(e) => set("address_country", e.target.value)} />
                    <input style={inp} placeholder={t("street")} value={form.street} onChange={(e) => set("street", e.target.value)} />
                    <input style={inp} placeholder={t("city")} value={form.city} onChange={(e) => set("city", e.target.value)} />
                    <input style={inp} placeholder={t("region")} value={form.region} onChange={(e) => set("region", e.target.value)} />
                    <input style={inp} placeholder={t("post_code")} value={form.post_code} onChange={(e) => set("post_code", e.target.value)} />
                  </>
                )}
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12, alignItems: "center" }}>
            <button className="btn primary small" type="submit">{t("save")}</button>
            <button className="btn secondary small" type="button" onClick={() => setAdding(false)}>{t("cancel")}</button>
            {rate && rate.base_rate != null && <span className="muted" style={{ fontSize: 13 }}>{formatPrice(rate.base_rate, cur)}{t("per_night")}</span>}
            {err && <span style={{ color: "#A32D2D", fontSize: 13 }}>{err}</span>}
          </div>
        </form>
      )}

      {/* list */}
      {rows == null ? (
        <p className="muted" style={{ marginTop: 16 }}>…</p>
      ) : rows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 16, textAlign: "center" }} className="muted">{t("none")}</div>
      ) : shownRows.length === 0 ? (
        <div style={{ ...CARD, padding: 20, marginTop: 16, textAlign: "center" }} className="muted">
          {lang === "am" ? "ተመሳሳይ አልተገኘም" : lang === "om" ? "Wanti walsimu hin argamne" : "No room or guest matches"} “{q}”
        </div>
      ) : (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {shownRows.map((r) => {
            const m = STMETA[r.status] || STMETA.booked;
            return (
              <div key={r.id} id={"res-" + r.id} style={{ ...CARD, padding: 14, ...(String(focusId) === String(r.id) ? { boxShadow: "0 0 0 2px #0F6E56" } : {}) }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700, fontSize: 16 }}>{r.guest_name}</span>
                      <span style={{ background: m.bg, color: m.fg, borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{t("st_" + r.status)}</span>
                      {r.is_6pm_hold && (r.status === "booked" || r.status === "waitlist") && <span style={{ background: "#FBF2DC", color: "#8A6D3B", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{t("hold6")}</span>}
                      {r.needs_room && (r.status === "booked" || r.status === "waitlist") && <span style={{ background: "#FDECEC", color: "#A32D2D", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{t("needs_room")}</span>}
                      {r.residency === "foreigner" && <span style={{ background: "#EAF3EC", color: "#1D7A44", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>{r.fx_currency && r.fx_currency !== "ETB" ? r.fx_currency : (lang === "am" ? "የውጭ" : lang === "om" ? "Alagaa" : "Foreign")}</span>}
                      {r.bill_to_company && <span style={{ fontSize: 12, color: "#5b6472" }}>· {t("bill_company")}{r.company_name ? `: ${r.company_name}` : ""}</span>}
                      {r.precheckin_at && <span style={{ background: "#E7F3EE", color: "#0F6E56", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 }}>✓ {t("precheckin_badge")}</span>}
                    </div>
                    <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
                      {r.room_type_name || "—"}{r.room_name ? ` · ${r.room_name}` : ""} · {r.check_in_date} → {r.check_out_date} · {r.nights} {r.nights === 1 ? t("night") : t("nights")}
                      {r.guest_phone ? ` · ${r.guest_phone}` : ""}{r.origin ? ` · ${r.origin}` : ""}
                    </div>
                    {(r.arrival_time || r.special_requests) && (
                      <div className="muted" style={{ fontSize: 12.5, marginTop: 2, color: "#0F6E56" }}>
                        {r.arrival_time ? `${t("arrival_time_l")}: ${r.arrival_time}` : ""}{r.arrival_time && r.special_requests ? " · " : ""}{r.special_requests ? `${t("special_req_l")}: ${r.special_requests}` : ""}
                      </div>
                    )}
                    {checkinOn && r.has_id_image && (
                      <div style={{ marginTop: 3 }}>
                        <button onClick={() => viewIdImage(r)} style={linkBtn("#3C3489")}>{t("view_id")}</button>
                      </div>
                    )}
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, color: "#27500A" }}>{formatPrice(r.total, cur)}</div>
                    {r.balance_due > 0 && <div style={{ fontSize: 12, color: "#8A5A0B" }}>{t("balance")}: {formatPrice(r.balance_due, cur)}</div>}
                  </div>
                </div>
                {canEdit && (r.status === "booked" || r.status === "checked_in" || r.status === "checked_out") && (
                  <div style={{ display: "flex", gap: 12, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
                    {r.status === "booked" && <button className="btn primary small" onClick={() => openCheckin(r)}>{t("check_in")}</button>}
                    {r.status === "booked" && !r.room_id && <button className="btn secondary small" onClick={() => openAssign(r)}>{t("assign_room")}</button>}
                    {r.status === "checked_in" && <button className="btn primary small" onClick={() => openCheckout(r)}>{t("check_out")}</button>}
                    <button onClick={() => toggleFolio(r)} style={linkBtn("#0F6E56")}>{t("folio")}</button>
                    {(r.status === "booked" || r.status === "checked_in") && <button onClick={() => (editing === r.id ? setEditing(null) : openEdit(r))} style={linkBtn("#0F6E56")}>{t("edit")}</button>}
                    {(r.status === "booked" || r.status === "checked_in") && r.room_id && <button onClick={() => openMove(r)} style={linkBtn("#0F6E56")}>{t("move_room")}</button>}
                    {(r.status === "booked" || r.status === "checked_in") && <button onClick={() => openExtend(r)} style={linkBtn("#7A4EA8")}>{t("extend_stay")}</button>}
                    {(r.status === "booked" || r.status === "checked_in") && <button onClick={() => toggleServices(r)} style={linkBtn("#8A6D3B")}>{t("services")}</button>}
                    {(r.status === "checked_in" || r.status === "checked_out") && <button onClick={() => (tinFor === r.id ? setTinFor(null) : openTin(r))} style={linkBtn("#3C3489")}>{t("receipt")}</button>}
                    {(r.status === "booked" || r.status === "checked_in") && <button onClick={() => doDiscount(r)} style={linkBtn("#185FA5")}>{t("discount")}</button>}
                    {r.status === "booked" && <button onClick={() => doNoShow(r)} style={linkBtn("#8A5A0B")}>{t("act_no_show")}</button>}
                    {checkinOn && r.status === "booked" && <button onClick={() => showPrecheckinLink(r)} style={linkBtn("#0F6E56")}>{t("precheckin_link")}</button>}
                    {messagingOn && msgCfg && msgCfg.configured && (r.status === "booked" || r.status === "checked_in") && <button onClick={() => (msgFor === r.id ? setMsgFor(null) : openMsg(r))} style={linkBtn("#0088cc")}>{t("message_l")}{r.messaging_linked ? " ✓" : ""}</button>}
                    {messagingOn && msgCfg && msgCfg.configured && (r.status === "booked" || r.status === "checked_in") && <button onClick={() => (connectFor === r.id ? setConnectFor(null) : openConnect(r))} style={linkBtn("#0077b3")}>📱 {t("connect_l")}{r.messaging_linked ? " ✓" : ""}</button>}
                    {(r.status === "booked" || r.status === "checked_in") && <button onClick={() => doCancel(r)} style={linkBtn("#A32D2D")}>{t("act_cancel")}</button>}
                  </div>
                )}
                {checkinOn && linkFor === r.id && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8, alignItems: "center", background: "#E7F3EE", border: "1px solid #cfe6db", borderRadius: 8, padding: 8 }}>
                    <span style={{ fontSize: 12, color: "#0F6E56", fontWeight: 600 }}>{t("link_hint")}</span>
                    <input readOnly style={{ ...inp, flex: 1, minWidth: 220, fontSize: 12 }} value={linkUrl} onFocus={(e) => e.target.select()} />
                    <button className="btn primary small" onClick={copyLink}>{linkCopied ? t("copied") : t("copy")}</button>
                    <button className="btn secondary small" onClick={() => setLinkFor(null)}>{t("close_l")}</button>
                  </div>
                )}
                {messagingOn && msgFor === r.id && (
                  <div style={{ marginTop: 8, background: "#EAF6FC", border: "1px solid #cbe6f5", borderRadius: 8, padding: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#0077b3", marginBottom: 6 }}>✈ {t("tg_title")}</div>
                    {!r.messaging_linked ? (
                      <div style={{ fontSize: 13 }}>
                        <div className="muted" style={{ marginBottom: 6 }}>{t("tg_notlinked")}</div>
                        {optIn && optIn.url ? (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                            <input readOnly style={{ ...inp, flex: 1, minWidth: 220, fontSize: 12 }} value={optIn.url} onFocus={(e) => e.target.select()} />
                            <button className="btn primary small" onClick={() => { try { navigator.clipboard.writeText(optIn.url); setMsgFlash(t("copied")); } catch { /* ignore */ } }}>{t("copy")}</button>
                          </div>
                        ) : <div className="muted" style={{ fontSize: 12.5 }}>{t("tg_setup_first")}</div>}
                      </div>
                    ) : (
                      <div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                          {checkinOn && <button className="btn small" onClick={() => sendCheckin(r)}>{t("tg_send_checkin")}</button>}
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                          {MSG_TEMPLATES.map((tpl, i) => (
                            <button key={i} onClick={() => setMsgText(tpl)} style={linkBtn("#0088cc")}>{t("tg_tpl" + (i + 1))}</button>
                          ))}
                        </div>
                        <textarea style={{ ...inp, width: "100%", minHeight: 54 }} placeholder={t("tg_placeholder")} value={msgText} onChange={(e) => setMsgText(e.target.value)} />
                        <div style={{ marginTop: 6 }}>
                          <button className="btn primary small" disabled={!msgText.trim()} onClick={() => sendMsg(r)}>{t("tg_send")}</button>
                        </div>
                      </div>
                    )}
                    {msgFlash && <div style={{ fontSize: 12.5, color: "#0077b3", marginTop: 6 }}>{msgFlash}</div>}
                    {msgLog.length > 0 && (
                      <div style={{ marginTop: 8, borderTop: "1px solid #cbe6f5", paddingTop: 6 }}>
                        {msgLog.slice(0, 5).map((m) => (
                          <div key={m.id} style={{ fontSize: 12, color: m.status === "sent" ? "#4a5560" : "#A32D2D", padding: "1px 0" }}>
                            {m.status === "sent" ? "✓" : "✕"} {(m.body || "").slice(0, 80)}{(m.body || "").length > 80 ? "…" : ""}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {messagingOn && connectFor === r.id && (
                  <div style={{ marginTop: 8, background: "#EAF6FC", border: "1px solid #cbe6f5", borderRadius: 8, padding: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#0077b3", marginBottom: 6 }}>📱 {t("connect_title")}</div>
                    {r.messaging_linked || (connect && connect.linked) ? (
                      <div style={{ fontSize: 13, color: "#0F6E56", fontWeight: 600 }}>✓ {t("connected_l")}</div>
                    ) : connect && connect.url ? (
                      <div>
                        <div className="muted" style={{ fontSize: 12.5, marginBottom: 8 }}>{t("connect_hint")}</div>
                        {connect.qr_code_url && (
                          <img src={connect.qr_code_url} alt={t("connect_title")} style={{ width: 160, height: 160, display: "block", marginBottom: 8 }} />
                        )}
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                          <input readOnly style={{ ...inp, flex: 1, minWidth: 220, fontSize: 12 }} value={connect.url} onFocus={(e) => e.target.select()} />
                          <button className="btn primary small" onClick={() => { try { navigator.clipboard.writeText(connect.url); } catch { /* ignore */ } }}>{t("copy")}</button>
                        </div>
                      </div>
                    ) : <div className="muted" style={{ fontSize: 12.5 }}>{t("tg_setup_first")}</div>}
                  </div>
                )}
                {canEdit && (r.status === "waitlist" || r.status === "cancelled" || r.status === "no_show") && (
                  <div style={{ display: "flex", gap: 12, marginTop: 10, alignItems: "center", flexWrap: "wrap" }}>
                    <button className="btn secondary small" onClick={() => doReinstate(r)}>{t("act_reinstate")}</button>
                    <button onClick={() => toggleFolio(r)} style={linkBtn("#0F6E56")}>{t("folio")}</button>
                  </div>
                )}
                {folioId === r.id && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{t("folio")}</div>
                    <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
                      <tbody>
                        {(() => {
                          // DISPLAY-only grouping of folio lines by revenue point. The
                          // accommodation group is the computed room line (sole member);
                          // stored charges group by their `source`. Subtotals here are
                          // per-group presentation only — the service charge / VAT /
                          // total / balance rows below are unchanged.
                          const hdrStyle = { padding: "6px 0 1px", fontWeight: 600, fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".3px", color: "#5b6472" };
                          const subLbl = { padding: "1px 0 4px", fontSize: 12, color: "#5b6472" };
                          const subAmt = { padding: "1px 0 4px", textAlign: "right", fontSize: 12, color: "#5b6472" };
                          const catOf = (c) => (["fnb", "wellness", "roomservice"].includes(c.source) ? c.source : "other");
                          const groups = { fnb: [], wellness: [], roomservice: [], other: [] };
                          (r.charges || []).forEach((c) => { groups[catOf(c)].push(c); });
                          const roomAmt = r.room_charge != null ? r.room_charge : (r.rate_per_night || 0) * r.nights;
                          const labels = { accommodation: t("cat_accommodation"), fnb: t("cat_fnb"), wellness: t("cat_wellness"), roomservice: t("cat_roomservice"), other: t("cat_other") };
                          const out = [];
                          // Accommodation group (room/rate line is the sole member).
                          out.push(<tr key="g-acc-h"><td colSpan={3} style={hdrStyle}>{labels.accommodation}</td></tr>);
                          out.push(<tr key="acc-room"><td style={{ padding: "2px 0" }}>{t("room_line")} × {r.nights}</td><td style={{ textAlign: "right" }}>{formatPrice(roomAmt, cur)}</td><td style={{ width: 22 }}></td></tr>);
                          out.push(<tr key="acc-sub"><td style={subLbl}>{t("group_subtotal")}</td><td style={subAmt}>{formatPrice(roomAmt, cur)}</td><td></td></tr>);
                          ["fnb", "wellness", "roomservice", "other"].forEach((g) => {
                            const items = groups[g];
                            if (!items.length) return;
                            out.push(<tr key={`g-${g}-h`}><td colSpan={3} style={hdrStyle}>{labels[g]}</td></tr>);
                            items.forEach((c) => {
                              out.push(
                                <tr key={c.id}>
                                  <td style={{ padding: "2px 0" }}>
                                    {c.description}
                                    {c.approved_at && <span style={{ marginLeft: 6, color: "#1D7A44", fontSize: 11, fontWeight: 600 }}>✓ {c.approval_method === "signed" ? t("signed") : t("approved_app")}</span>}
                                  </td>
                                  <td style={{ textAlign: "right" }}>{formatPrice(c.amount, cur)}</td>
                                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                                    {["fnb", "wellness", "roomservice"].includes(c.source) && <button onClick={() => printVoucher(r, c)} title={t("v_title")} style={{ background: "none", border: "none", color: "#1D7A44", cursor: "pointer", fontSize: 14, marginRight: 4 }}>🧾</button>}
                                    {canEdit && !c.approved_at && r.status !== "checked_out" && <button onClick={() => markSigned(r, c.id)} title={t("mark_signed")} style={{ background: "none", border: "none", color: "#1D7A44", cursor: "pointer", fontSize: 14, marginRight: 4 }}>✍</button>}
                                    {canEdit && !c.approved_at && r.status !== "checked_out" && <button onClick={() => reqApproval(r, c.id)} title={t("request_approval")} style={{ background: "none", border: "none", color: "#0088cc", cursor: "pointer", fontSize: 14, marginRight: 4 }}>📲</button>}
                                    {canEdit && r.status !== "checked_out" && <button onClick={() => setXfer({ cid: c.id, target: "" })} title={t("transfer")} style={{ background: "none", border: "none", color: "#185FA5", cursor: "pointer", fontSize: 14, marginRight: 4 }}>⇄</button>}
                                    {canEdit && r.status !== "checked_out" && <button onClick={() => delCharge(r, c.id)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 15 }}>×</button>}
                                  </td>
                                </tr>
                              );
                              // Itemised food/drink lines under an F&B charge that
                              // was posted to the folio (display-only breakdown).
                              (c.detail || []).forEach((ln, li) => {
                                out.push(
                                  <tr key={`${c.id}-d${li}`}>
                                    <td style={{ padding: "0 0 2px 14px", fontSize: 11.5, color: "#7a828e" }}>
                                      {ln.name}{ln.qty ? ` × ${ln.qty}` : ""}
                                    </td>
                                    <td style={{ textAlign: "right", fontSize: 11.5, color: "#7a828e" }}>{formatPrice(ln.amount, cur)}</td>
                                    <td></td>
                                  </tr>
                                );
                              });
                            });
                            const sub = items.reduce((s, c) => s + (c.amount || 0), 0);
                            out.push(<tr key={`g-${g}-s`}><td style={subLbl}>{t("group_subtotal")}</td><td style={subAmt}>{formatPrice(sub, cur)}</td><td></td></tr>);
                          });
                          return out;
                        })()}
                        {r.discount_amount > 0 && <tr><td>{t("discount_l")}</td><td style={{ textAlign: "right", color: "#A32D2D" }}>-{formatPrice(r.discount_amount, cur)}</td><td></td></tr>}
                        {r.service_charge > 0 && <tr><td className="muted">{t("service_charge_l")} {r.service_pct ? `${r.service_pct}%` : ""}</td><td style={{ textAlign: "right" }}>{formatPrice(r.service_charge, cur)}</td><td></td></tr>}
                        {r.tax_amount > 0 && <tr><td className="muted">{r.tax_label || t("tax_l")}</td><td style={{ textAlign: "right" }}>{formatPrice(r.tax_amount, cur)}</td><td></td></tr>}
                        <tr style={{ borderTop: "1px solid #ddd", fontWeight: 700 }}><td style={{ paddingTop: 5 }}>{t("grand_total")}</td><td style={{ textAlign: "right", paddingTop: 5 }}>{formatPrice(r.total, cur)}</td><td></td></tr>
                        {r.balance_due > 0 && <tr><td style={{ color: "#8A5A0B" }}>{t("balance")}</td><td style={{ textAlign: "right", color: "#8A5A0B" }}>{formatPrice(r.balance_due, cur)}</td><td></td></tr>}
                      </tbody>
                    </table>
                    {canEdit && xfer && (() => {
                      const targets = rows.filter((x) => x.id !== r.id && (x.status === "booked" || x.status === "checked_in"));
                      return (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, alignItems: "center", background: "#F4F7F7", padding: 8, borderRadius: 8 }}>
                        <span style={{ fontSize: 13 }}>{t("transfer_to")}</span>
                        {targets.length === 0 ? (
                          <span className="muted" style={{ fontSize: 12.5 }}>{t("transfer_none")}</span>
                        ) : (
                          <>
                            <select style={inp} value={xfer.target} onChange={(e) => setXfer({ ...xfer, target: e.target.value })}>
                              <option value="">{t("transfer_pick")}</option>
                              {targets.map((x) => (
                                <option key={x.id} value={x.id}>{x.guest_name}{x.room_name ? ` · ${x.room_name}` : ""}</option>
                              ))}
                            </select>
                            <button className="btn primary small" onClick={() => doTransfer(r)} disabled={!xfer.target}>{t("transfer")}</button>
                          </>
                        )}
                        <button className="btn secondary small" onClick={() => setXfer(null)}>{t("cancel")}</button>
                      </div>
                      );
                    })()}
                    {canEdit && r.status !== "checked_out" && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, alignItems: "center" }}>
                        <input style={{ ...inp, flex: 1, minWidth: 130 }} placeholder={t("charge_desc")} value={cf.description} onChange={(e) => setCf({ ...cf, description: e.target.value })} />
                        <select style={inp} value={cf.kind} onChange={(e) => setCf({ ...cf, kind: e.target.value })}>
                          <option value="fb">{t("k_fb")}</option>
                          <option value="laundry">{t("k_laundry")}</option>
                          <option value="minibar">{t("k_minibar")}</option>
                          <option value="transport">{t("k_transport")}</option>
                          <option value="other">{t("k_other")}</option>
                          <option value="paid_out">{t("k_paid_out")}</option>
                          <option value="rebate">{t("k_rebate")}</option>
                          <option value="refund">{t("k_refund")}</option>
                        </select>
                        <select style={inp} value={cf.source} onChange={(e) => setCf({ ...cf, source: e.target.value })} title={t("cat_other")}>
                          <option value="fnb">{t("cat_fnb")}</option>
                          <option value="wellness">{t("cat_wellness")}</option>
                          <option value="roomservice">{t("cat_roomservice")}</option>
                          <option value="other">{t("cat_other")}</option>
                        </select>
                        <input style={{ ...inp, width: 110 }} type="number" min="0" placeholder={t("charge_amt")} value={cf.amount} onChange={(e) => setCf({ ...cf, amount: e.target.value })} />
                        <button className="btn primary small" onClick={() => submitCharge(r)}>{t("add_charge")}</button>
                      </div>
                    )}
                    {upsellOn && r.status !== "checked_out" && (
                      <div style={{ marginTop: 10, background: "#FFF8E8", border: "1px solid #F0E4BC", borderRadius: 8, padding: 8 }}>
                        {upsellFor !== r.id ? (
                          <button className="btn small" onClick={() => loadUpsell(r)}>★ {t("upsell")}</button>
                        ) : (
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: "#8A5A0B" }}>★ {t("upsell")}</div>
                            {(upOpts.upgrades || []).length > 0 && (
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 6 }}>
                                {upOpts.upgrades.map((u) => (
                                  <button key={u.room_type_id} className="btn small" onClick={() => acceptUpsell(r, { kind: "upgrade", label: u.name, amount: u.premium_total, room_type_id: u.room_type_id })}>
                                    {u.name} +{formatPrice(u.premium_total, cur)}
                                  </button>
                                ))}
                              </div>
                            )}
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                              <select style={inp} value={uf.label} onChange={(e) => setUf({ ...uf, label: e.target.value })}>
                                <option value="">{t("extra_l")}</option>
                                {(upOpts.extras || []).map((x) => <option key={x.key} value={x.label}>{x.label}</option>)}
                              </select>
                              <input style={{ ...inp, width: 100 }} type="number" min="0" placeholder={t("amount")} value={uf.amount} onChange={(e) => setUf({ ...uf, amount: e.target.value })} />
                              <input style={{ ...inp, width: 78 }} type="number" min="0" max="100" placeholder="%" title={t("commission")} value={uf.commission} onChange={(e) => setUf({ ...uf, commission: e.target.value })} />
                              <button className="btn primary small" disabled={!uf.label || !uf.amount} onClick={() => acceptUpsell(r, { kind: "extra", label: uf.label, amount: Number(uf.amount), commission_pct: uf.commission === "" ? 0 : Number(uf.commission) })}>{t("addU")}</button>
                              <button className="btn secondary small" onClick={() => setUpsellFor(null)}>{t("cancel")}</button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    {feedbackOn && (
                      <div style={{ marginTop: 10, background: "#EEF6FF", border: "1px solid #cfe4fb", borderRadius: 8, padding: 8 }}>
                        {fbFor !== r.id ? (
                          <button className="btn small" onClick={() => { setFbFor(r.id); setFf({ overall: "", staff: "", comment: "" }); }}>★ {t("feedback")}</button>
                        ) : (
                          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                            <span style={{ fontSize: 12.5, color: "#0C447C", fontWeight: 600 }}>★ {t("feedback")}</span>
                            <select style={{ ...inp, width: 118 }} value={ff.overall} onChange={(e) => setFf({ ...ff, overall: e.target.value })}>
                              <option value="">{t("overall")}</option>
                              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
                            </select>
                            <select style={{ ...inp, width: 110 }} value={ff.staff} onChange={(e) => setFf({ ...ff, staff: e.target.value })}>
                              <option value="">{t("staff_r")}</option>
                              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
                            </select>
                            <input style={{ ...inp, flex: 1, minWidth: 120 }} placeholder={t("fb_comment")} value={ff.comment} onChange={(e) => setFf({ ...ff, comment: e.target.value })} />
                            <button className="btn primary small" disabled={!ff.overall} onClick={() => submitFeedback(r)}>{t("save_fb")}</button>
                            <button className="btn secondary small" onClick={() => setFbFor(null)}>{t("cancel")}</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                {svcId === r.id && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{t("services")}</div>
                    {svcList.length === 0 ? <div className="muted" style={{ fontSize: 13 }}>{t("no_services")}</div> : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {svcList.map((s) => (
                          <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13.5 }}>
                            <span style={{ fontWeight: 700, color: "#8A6D3B", minWidth: 64 }}>{t("sv_" + s.kind)}</span>
                            <span style={{ flex: 1, minWidth: 0, textDecoration: s.status === "done" ? "line-through" : "none", color: s.status === "done" ? "#8A949B" : "#24313A" }}>{s.detail || "—"}{s.due_at ? ` · ${new Date(s.due_at).toLocaleString()}` : ""}</span>
                            <button className="btn secondary small" onClick={() => doneSvc(r, s)}>{s.status === "done" ? t("reopen") : t("done_l")}</button>
                            <button onClick={() => delSvc(r, s.id)} style={{ background: "none", border: "none", color: "#A32D2D", cursor: "pointer", fontSize: 15 }}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                    {canEdit && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, alignItems: "center" }}>
                        <select style={inp} value={sf.kind} onChange={(e) => setSf({ ...sf, kind: e.target.value })}>
                          <option value="request">{t("sv_request")}</option>
                          <option value="wakeup">{t("sv_wakeup")}</option>
                          <option value="message">{t("sv_message")}</option>
                        </select>
                        <input style={{ ...inp, flex: 1, minWidth: 140 }} placeholder={t("sv_detail")} value={sf.detail} onChange={(e) => setSf({ ...sf, detail: e.target.value })} />
                        {sf.kind === "wakeup" && <input type="datetime-local" style={inp} value={sf.due_at} onChange={(e) => setSf({ ...sf, due_at: e.target.value })} />}
                        <button className="btn primary small" onClick={() => addSvc(r)}>{t("add_service")}</button>
                      </div>
                    )}
                  </div>
                )}
                {panel && panel.id === r.id && (panel.type === "checkin" || panel.type === "checkout") && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 4 }}>{panel.type === "checkin" ? t("ci_title") : t("co_title")}</div>
                    <div style={{ fontSize: 12.5, color: "#5f6b78", marginBottom: 8 }}>
                      {lang === "am" ? "ጠቅላላ" : lang === "om" ? "Ida'ama" : "Total"}: {formatPrice(r.total, cur)}
                      {" · "}{lang === "am" ? "የተከፈለ" : lang === "om" ? "Kaffalame" : "Paid"}: {formatPrice(r.paid_amount || 0, cur)}
                      {" · "}<b style={{ color: r.balance_due > 0 ? "#8A5A0B" : "#1D7A44" }}>{t("balance")}: {formatPrice(r.balance_due || 0, cur)}</b>
                      {panel.type === "checkin" && <span> — {lang === "am" ? "ክፍያ በመግቢያ አማራጭ ነው" : lang === "om" ? "kaffaltiin ga'umsatti filannoo dha" : "payment at check-in is an optional deposit"}</span>}
                    </div>
                    {panel.type === "checkin" && r.company_hold && (
                      <div style={{ marginBottom: 8 }}>
                        <div style={{ fontSize: 12, color: "#8A5A0B", marginBottom: 4 }}>
                          {lang === "am" ? "በድርጅት ስም የተያዘ — የእንግዳውን ስም ያስገቡ (ደረሰኝ ለድርጅቱ ይቀራል)" : lang === "om" ? "Maqaa dhaabbataan qabame — maqaa keessummaa galchi (nagaheen dhaabbataaf hafa)" : "Held in company name — enter the actual guest (receipt stays with the company)"}
                        </div>
                        <input style={{ ...inp, width: "100%" }} placeholder={lang === "am" ? "የእንግዳ ስም" : lang === "om" ? "Maqaa keessummaa" : "Guest name"}
                          value={pf.guest_name} onChange={(e) => setPf({ ...pf, guest_name: e.target.value })} />
                      </div>
                    )}
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
                      {panel.type === "checkin" && (
                        <>
                          <RoomPicker t={t} value={pf.room_id} onChange={(id) => setPf({ ...pf, room_id: id })}
                            rooms={availCheckin || rooms.filter((rm) => !r.room_type_id || String(rm.room_type_id) === String(r.room_type_id)).map((x) => ({ ...x, available: true, conflict: null }))} />
                          {rooms.length === 0 && (
                            <div style={{ fontSize: 12, color: "#8A5A0B", marginTop: 4 }}>
                              {lang === "am" ? "እስካሁን ክፍል የለም — በፊት ዴስክ → ክፍሎች ውስጥ ይጨምሩ።" : lang === "om" ? "Kutaan hin jiru — Fuula Duraa → Kutaalee jalatti dabali." : "No rooms set up yet — add them under Front Desk → Rooms."}
                            </div>
                          )}
                        </>
                      )}
                      <select style={inp} value={pf.method} onChange={(e) => setPf({ ...pf, method: e.target.value })}>
                        <option value="cash">{t("pm_cash")}</option>
                        <option value="telebirr">{t("pm_telebirr")}</option>
                        <option value="cbe_birr">{t("pm_cbe_birr")}</option>
                        <option value="pos">{t("pm_pos")}</option>
                        <option value="other">{t("pm_other")}</option>
                      </select>
                      <input style={inp} placeholder={lang === "am" ? "ማጣቀሻ (ደረሰኝ/ግብይት ቁ.)" : lang === "om" ? "Wabii (lakk. daldalaa)" : "Reference (txn / slip no.)"} value={pf.reference} onChange={(e) => setPf({ ...pf, reference: e.target.value })} />
                      <input style={inp} type="number" min="0"
                        placeholder={panel.type === "checkin" ? (lang === "am" ? "ተቀማጭ (አማራጭ)" : lang === "om" ? "Qarshii (filannoo)" : "Deposit (optional)") : (lang === "am" ? "የሚከፈል መጠን" : lang === "om" ? "Hamma kaffalamu" : "Amount to settle")}
                        value={pf.amount} onChange={(e) => setPf({ ...pf, amount: e.target.value })} />
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                      <button className="btn primary small" onClick={panel.type === "checkin" ? confirmCheckin : confirmCheckout}>{t("confirm")}</button>
                      <button className="btn secondary small" onClick={closePanel}>{t("cancel")}</button>
                      {panelErr && <span style={{ color: "#A32D2D", fontSize: 13 }}>{panelErr}</span>}
                    </div>
                  </div>
                )}
                {panel && panel.id === r.id && panel.type === "move" && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{t("move_title")}</div>
                    <div style={{ maxWidth: 320 }}>
                      <RoomPicker t={t} value={mf.room_id} onChange={(id) => setMf({ ...mf, room_id: id })}
                        rooms={(availCheckin || rooms.map((x) => ({ ...x, available: true, conflict: null }))).filter((rm) => String(rm.id) !== String(r.room_id))} />
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                      <button className="btn primary small" onClick={confirmMove}>{t("confirm")}</button>
                      <button className="btn secondary small" onClick={closePanel}>{t("cancel")}</button>
                      {panelErr && <span style={{ color: "#A32D2D", fontSize: 13 }}>{panelErr}</span>}
                    </div>
                  </div>
                )}
                {panel && panel.id === r.id && panel.type === "assign" && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{t("assign_room")}</div>
                    <div style={{ maxWidth: 320 }}>
                      <RoomPicker t={t} value={mf.room_id} onChange={(id) => setMf({ ...mf, room_id: id })}
                        rooms={availCheckin || rooms.filter((rm) => !r.room_type_id || String(rm.room_type_id) === String(r.room_type_id)).map((x) => ({ ...x, available: true, conflict: null }))} />
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                      <button className="btn primary small" onClick={confirmAssign}>{t("confirm")}</button>
                      <button className="btn secondary small" onClick={closePanel}>{t("cancel")}</button>
                      {panelErr && <span style={{ color: "#A32D2D", fontSize: 13 }}>{panelErr}</span>}
                    </div>
                  </div>
                )}
                {panel && panel.id === r.id && panel.type === "extend" && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{t("extend_title")}</div>
                    <label style={{ fontSize: 12, color: "#5f6b78" }}>{t("new_checkout")}
                      <input type="date" style={{ ...inp, display: "block", marginTop: 3, maxWidth: 200 }} min={r.check_in_date}
                        value={mf.check_out_date} onChange={(e) => setMf({ ...mf, check_out_date: e.target.value })} />
                    </label>
                    <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                      <button className="btn primary small" onClick={confirmExtend}>{t("confirm")}</button>
                      <button className="btn secondary small" onClick={closePanel}>{t("cancel")}</button>
                      {panelErr && <span style={{ color: "#A32D2D", fontSize: 13 }}>{panelErr}</span>}
                    </div>
                  </div>
                )}
                {canEdit && editing === r.id && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{t("edit_title")}</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }}>
                      <select style={{ ...inp, maxWidth: 110 }} value={ef.title || ""} onChange={(e) => setEf({ ...ef, title: e.target.value })}>
                        <option value="">{lang === "am" ? "ማዕረግ" : lang === "om" ? "Sadarkaa" : "Title"}</option>
                        {["Mr", "Mrs", "Ms", "Ato", "W/ro", "W/rt", "Dr", "Prof"].map((x) => <option key={x} value={x}>{x}</option>)}
                      </select>
                      {ef.structured ? (
                        <>
                          <input style={inp} placeholder={t("fname")} value={ef.first_name} onChange={(e) => setEf({ ...ef, first_name: e.target.value })} />
                          <input style={inp} placeholder={t("mname")} value={ef.middle_name} onChange={(e) => setEf({ ...ef, middle_name: e.target.value })} />
                          <input style={inp} placeholder={t("lname")} value={ef.last_name} onChange={(e) => setEf({ ...ef, last_name: e.target.value })} />
                        </>
                      ) : (
                        <input style={inp} placeholder={t("guest")} value={ef.guest_name} onChange={(e) => setEf({ ...ef, guest_name: e.target.value })} />
                      )}
                      <input style={inp} placeholder={t("phone")} value={ef.guest_phone} onChange={(e) => setEf({ ...ef, guest_phone: e.target.value })} />
                      <input style={inp} placeholder={t("nationality")} value={ef.nationality} onChange={(e) => setEf({ ...ef, nationality: e.target.value })} />
                      <select style={inp} value={ef.id_type} onChange={(e) => setEf({ ...ef, id_type: e.target.value })}>
                        <option value="">{t("id_type")}: {t("id_none")}</option>
                        <option value="kebele">{t("id_kebele")}</option>
                        <option value="fayda">{t("id_fayda")}</option>
                        <option value="passport">{t("id_passport")}</option>
                        <option value="other">{t("id_other")}</option>
                      </select>
                      <input style={inp} placeholder={t("id_number")} value={ef.id_number} onChange={(e) => setEf({ ...ef, id_number: e.target.value })} />
                      <input style={inp} placeholder={t("origin")} value={ef.origin} onChange={(e) => setEf({ ...ef, origin: e.target.value })} />
                      <input style={inp} placeholder={t("customer_tin")} value={ef.bill_to_tin} onChange={(e) => setEf({ ...ef, bill_to_tin: e.target.value })} />
                      <input style={inp} placeholder={t("company")} value={ef.company_name} onChange={(e) => setEf({ ...ef, company_name: e.target.value })} />
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                      <button className="btn primary small" onClick={() => saveEdit(r)}>{t("save_edit")}</button>
                      <button className="btn secondary small" onClick={() => setEditing(null)}>{t("cancel")}</button>
                      {editErr && <span style={{ color: "#A32D2D", fontSize: 13 }}>{editErr}</span>}
                    </div>
                  </div>
                )}
                {canEdit && tinFor === r.id && (
                  <div style={{ marginTop: 10, borderTop: "1px solid #eef2f2", paddingTop: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{t("receipt")}</div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
                      <input style={{ ...inp, minWidth: 200, flex: "1 1 200px" }} placeholder={t("tin_prompt")} value={tf.bill_to_tin} onChange={(e) => setTf({ ...tf, bill_to_tin: e.target.value })} />
                      <input style={{ ...inp, minWidth: 200, flex: "1 1 200px" }} placeholder={t("bill_to_name")} value={tf.company_name} onChange={(e) => setTf({ ...tf, company_name: e.target.value })} />
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                      <button className="btn primary small" onClick={() => doPrintBill(r)}>{t("print_bill")}</button>
                      <button className="btn secondary small" onClick={() => setTinFor(null)}>{t("cancel")}</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
